import type { SteerIntent } from '../../sim/mower';
import Phaser from 'phaser';
import { BIG_TREE_CROWN, DEFAULT_ART, TILE_BY_ID, type TileId } from '../../content/tiles';
import { dx, dy } from '../../sim/direction';
import type { SimEvent } from '../../sim/events';
import { BALL_TICKS, type Simulation } from '../../sim/simulation';
import { TILE_SIZE } from '../layout';
import { cameraCenter, mowerPixelPosition } from './interpolate';
import {
  CRITTER_FRAME,
  CRITTERS_KEY,
  MOLE_FRAME,
  MOWER_BROKEN_FRAME,
  mowerTextureKey,
  rippleWater,
  swayGrass,
  type SwayingFrame,
  PICKUP_FRAME,
  PICKUPS_KEY,
  HOSE_BIT,
  RIVAL_KEY,
  TILES_KEY,
} from './placeholderArt';
import { Effects, FX_FRAME, FX_KEY } from '../fx/Effects';
import { label, UI } from '../ui/text';
import type { BiomeId } from '../../content/biomes';
import { applyMood } from './Mood';
import { drawSurroundings } from './Surroundings';
import { frameIndex, SMASHED_FRAME, tileFrame, type TileFrameName } from './tileFrames';
import { TurnHints } from './TurnHints';
import { ECONOMY, smashCostFor } from '../../meta/economy';
import { Tile, type Tile as TileKind } from '../../sim/grid';
import type { HerdSpec } from '../../sim/level';
import { BOOST_BY_ID } from '../../content/boosts';
import { MAGNET_REACH } from '../../sim/boosts';
import { masterTrimTexture, paintedMowerTexture, stripeFrame } from './cosmeticArt';
import type { MowerId } from '../../content/mowers';
import { Darkness } from './Darkness';
import { Minimap, type MinimapMark } from './Minimap';
import { RING_1, SpriteOutline } from './outline';
import { CROWNS_KEY, GRAND_CROWNS_KEY } from './treeArt';
import { hillTextures } from './hillArt';
import { PROP_LIFT, propFor, propFrame, PROPS_KEY } from './props';
import { YardView } from './YardView';
import { YARD_DEPTH, type SecretYard } from '../../gen/secretYards';
import { mowerFx } from './mowerFx';
import { BLADES_KEY, swayBlades } from './bladesArt';

/** Where the mower sits vertically in the view while following (0 = top, 1 = bottom). */
const FOLLOW_ANCHOR = 0.55;
/** How far past a lawn's edges the camera may look: its fence and a strip of the world beyond. */
const EDGE_X = 6;
const EDGE_Y = 16;
/** Fraction of the remaining distance the camera closes per frame. */
const CAMERA_LERP = 0.18;

const isHose = (t: TileKind) => t === Tile.Hose || t === Tile.HoseChewed;
/** A goal's posts rise this far (pixels); its net reaches this far back. */
const GOAL_POST = 13;
const GOAL_NET = 7;
export const MOWER_DEPTH = 10;
/** The admire shine: over the ground and its stripes, under everything standing on it. */
const DEPTH_SHINE = MOWER_DEPTH - 4;
/** Above every lawn layer and effect (confetti sits at 60). */
const MOOD_DEPTH = 100;

/**
 * Draws a {@link Simulation}: the lawn as a tilemap layer, the mower as a sprite. Reads sim state
 * and reacts to sim events; never mutates the sim.
 */
/** Head to head: a dizzy mower's spin, a quarter turn this often (ms). */
export const DIZZY_SPIN_MS = 70;

export class LevelView {
  private readonly layer: Phaser.Tilemaps.TilemapLayer;
  private readonly mower: Phaser.GameObjects.Sprite;
  /** Head to head: an outline round this player's mower (`options.outline`). */
  private readonly mowerOutline: SpriteOutline | null = null;
  private readonly hints: TurnHints;
  private readonly pickups: (Phaser.GameObjects.Image | null)[];
  private readonly critterSprites: { img: Phaser.GameObjects.Image; hopTick: number }[];
  private readonly art: readonly TileId[];
  private readonly mapWidthPx: number;
  private readonly mapHeightPx: number;
  private camCenter: { x: number; y: number } | null = null;
  /** The lawn's secret yard (its gate and, once open, the garden). */
  private yard: YardView | null = null;
  /** Admiring the finished lawn: the camera is driven by `admireTo`, not the mower. */
  private admiring = false;
  private readonly fx: Effects;
  /** Flower beds the mower has run over (renderer state; the sim tile stays a flower bed). */
  private readonly trampled = new Set<number>();
  /** Sprinkler heads on the lawn, and the layer their spray is drawn on. */
  private sprinklers: { x: number; y: number }[] = [];
  /**
   * Garden hoses' free ends (owner, 2026-10-28): where water spouts from, which way, and every tile
   * of that hose (a chewed one stops the spout and squirts instead).
   */
  private hoseEnds: { x: number; y: number; dx: number; dy: number; tiles: number[] }[] = [];
  private spray!: Phaser.GameObjects.Graphics;
  /** Boss lawn art: the target stripes, as faint ticks (red where a mowed tile is wrong). */
  private artMarks: Phaser.GameObjects.Graphics | null = null;
  /** Sprinklers turned off with the key (drawn closed rather than broken). */
  private readonly sprinklersOff = new Set<number>();
  /** Cosmetic stripe pattern drawn on mowed tiles (null: the real mowing directions). */
  private readonly stripes: string | null;
  /** Running boosts: the shield bubble and the magnet's reach, drawn round the mower. */
  private boostFx!: Phaser.GameObjects.Graphics;
  private lastTurboPuff = 0;
  /** Boss rival: the robot mower, and a faint blue wash over the tiles it has mowed. */
  private rival: Phaser.GameObjects.Sprite | null = null;
  private rivalWash: Phaser.GameObjects.Graphics | null = null;
  /** Boss storm: the rain front, drawn over everything it has reached. */
  private rain: Phaser.GameObjects.Graphics | null = null;
  /** Chew marks over hose sections the mower ran over. */
  private readonly chewMarks = new Map<number, Phaser.GameObjects.Image>();
  /** Big trees' crowns, over the mower; each fades while the mower is beneath it. */
  private readonly crowns: { x: number; y: number; img: Phaser.GameObjects.Image }[] = [];
  /** Lawns bigger than the screen get a minimap. */
  private minimap: Minimap | null = null;
  /** Boss Lights Out: the night over the lawn. */
  private darkness: Darkness | null = null;
  /**
   * Standing props (three-quarter view), by tile index: each rises into the tile behind it, so it
   * draws over the mower when it is in a row nearer the camera than the mower's.
   */
  private readonly props = new Map<number, Phaser.GameObjects.Image>();
  private propRow = -1;
  /** Stray balls: the ball (drawn rolling) and its warning arrow and lane. */
  private ballFx: Phaser.GameObjects.Graphics | null = null;
  /** Mower soccer: the ball, the goals' graphics (top, bottom) and when the ball last popped up. */
  private pitchBallFx: Phaser.GameObjects.Graphics | null = null;
  private pitchGoals: Phaser.GameObjects.Graphics[] = [];
  private ballPoppedAt = -1e9;
  /** The world this lawn is in (its look: grand trees in the Meadows). */
  private readonly biome: BiomeId;
  /** The bees buzzing about each standing beehive, by tile index. */
  private readonly hiveBees = new Map<number, Phaser.GameObjects.Image[]>();
  /** Tall grass's standing blades, by tile index (bladesArt.ts). */
  private readonly blades = new Map<number, Phaser.GameObjects.Image>();
  /** Lawn items a mower smashed (they draw their broken frame; a trimmed one is plain ground). */
  private readonly smashedTiles = new Set<number>();
  /** Fallen trees' logs: the frame each buried tile draws (the sim only knows "obstacle"). */
  private readonly logs = new Map<number, TileFrameName>();
  /** Creaking trees: a sway on the tree and a shadow where it will land, until it falls. */
  private readonly creaks = new Map<number, Phaser.GameObjects.Graphics>();

  constructor(
    private readonly scene: Phaser.Scene,
    private sim: Simulation,
    /** Content art per tile (row-major); defaults to a generic look per sim tile kind. */
    art?: readonly TileId[],
    options: {
      reducedShake?: boolean;
      biome?: BiomeId;
      /** Cosmetics: the mower's paint and the stripe pattern (null for the standard look). */
      paint?: string | null;
      stripes?: string | null;
      /** The mower is mastered: it wears its gold Master trim. */
      master?: boolean;
      /** The lawn's secret yard, if it has one (gen/secretYards). */
      yard?: SecretYard | null;
      /** An outline round the mower in this color (head to head: "that one is mine"). */
      outline?: number;
    } = {},
  ) {
    this.biome = options.biome ?? 'backyard';
    // Lawn art is judged on the real stripes, so a pattern would hide the picture.
    this.stripes = sim.level.art ? null : (options.stripes ?? null);
    this.fx = new Effects(scene, MOWER_DEPTH - 1, options.reducedShake ?? false);
    const { grid } = sim;
    this.art =
      art ?? Array.from(grid.rawTiles(), (t) => DEFAULT_ART[t as keyof typeof DEFAULT_ART]);
    this.mapWidthPx = grid.width * TILE_SIZE;
    this.mapHeightPx = grid.height * TILE_SIZE;

    const map = scene.make.tilemap({
      tileWidth: TILE_SIZE,
      tileHeight: TILE_SIZE,
      width: grid.width,
      height: grid.height,
    });
    const tileset = map.addTilesetImage(TILES_KEY, TILES_KEY, TILE_SIZE, TILE_SIZE, 0, 0);
    if (!tileset) throw new Error('Tileset creation failed');
    const layer = map.createBlankLayer('lawn', tileset, 0, 0);
    if (!layer) throw new Error('Layer creation failed');
    this.layer = layer;
    for (let y = 0; y < grid.height; y++) {
      for (let x = 0; x < grid.width; x++) this.redrawTile(x, y);
    }

    drawSurroundings(
      scene,
      sim.level.id,
      options.biome ?? 'backyard',
      this.mapWidthPx,
      this.mapHeightPx,
      MOWER_DEPTH + 1,
    );
    const mouth = soccerMouthSpan(sim);
    drawPicketFence(scene, this.mapWidthPx, this.mapHeightPx, options.yard ?? null, mouth);
    if (options.yard) {
      this.yard = new YardView(scene, options.yard, this.standingDepth(options.yard.gate.y));
    }
    this.drawBuildings();
    this.drawHills();
    this.drawEdges();
    this.drawGoals();
    if (mouth) this.drawPitch(mouth);
    const shaded = this.plantCrowns();
    this.sprinklers = [];
    for (let y = 0; y < sim.grid.height; y++) {
      for (let x = 0; x < sim.grid.width; x++) {
        if (sim.grid.tileAt(x, y) === Tile.Sprinkler) this.sprinklers.push({ x, y });
      }
    }
    this.findHoseEnds();
    this.spray = scene.add.graphics().setDepth(MOWER_DEPTH + 2);
    this.boostFx = scene.add.graphics().setDepth(MOWER_DEPTH + 1);
    if (sim.level.art) this.artMarks = scene.add.graphics().setDepth(MOWER_DEPTH - 2);
    if (sim.level.art && !sim.level.art.goal) this.drawChalkOutline();
    if (sim.level.storm) {
      this.rain = scene.add.graphics().setDepth(MOWER_DEPTH + 4);
      drawShed(scene, this.mapWidthPx);
    }
    // Herding: the boss's pen, or a round-up match's pen at each end.
    for (const herd of sim.level.roundup?.pens ?? (sim.level.herd ? [sim.level.herd] : [])) {
      drawPenFence(scene, herd);
    }
    if (sim.rival) {
      this.rivalWash = scene.add.graphics().setDepth(MOWER_DEPTH - 3);
      this.rival = scene.add.sprite(0, 0, RIVAL_KEY, sim.rival.dir).setDepth(MOWER_DEPTH);
    }
    // The world's time of day, over everything on the lawn (effects included).
    applyMood(scene, options.biome ?? 'backyard', MOOD_DEPTH);
    if (sim.level.dark) this.darkness = new Darkness(scene, sim, this.art, MOWER_DEPTH + 6);
    if (sim.level.balls) this.ballFx = scene.add.graphics().setDepth(MOWER_DEPTH + 1);
    if (sim.level.soccer) this.pitchBallFx = scene.add.graphics().setDepth(MOWER_DEPTH - 0.5);
    const cam = scene.cameras.main;
    // (Not in the dark: a map would give the lawn away.)
    if (
      !sim.level.dark &&
      Minimap.wanted(this.mapWidthPx, this.mapHeightPx, cam.width, cam.height)
    ) {
      this.minimap = new Minimap(scene, sim, this.art, shaded, MOOD_DEPTH + 5);
    }

    this.pickups = (sim.level.pickups ?? []).map((p, i) =>
      sim.isCollected(i) ? null : this.makePickup(p, i),
    );

    this.critterSprites = sim.critters.list.map((c) => ({
      img: scene.add
        .image(
          (c.x + 0.5) * TILE_SIZE,
          (c.y + 0.5) * TILE_SIZE,
          CRITTERS_KEY,
          CRITTER_FRAME[c.kind],
        )
        .setDepth(MOWER_DEPTH - 1)
        .setAlpha(c.inside ? 0 : 1),
      hopTick: c.lastHopTick,
    }));

    const sheet = mowerSheet(
      scene,
      sim.mowerSpec.id as MowerId,
      options.paint ?? null,
      !!options.master,
    );
    // Made before the mower, so it draws under it at the same depth.
    if (options.outline !== undefined) {
      this.mowerOutline = new SpriteOutline(
        scene,
        sheet,
        [{ color: options.outline, offsets: RING_1 }],
        MOWER_DEPTH,
      );
    }
    this.mower = scene.add.sprite(0, 0, sheet, sim.mower.dir).setDepth(MOWER_DEPTH);
    this.hints = new TurnHints(scene, sim, MOWER_DEPTH);
    if (!options.reducedShake) this.startLawnLife();
    this.render(0);
  }

  /**
   * Big trees: a soft shade on the lawn under each crown, and the crown itself above the mower.
   * Returns the shaded tiles (row-major indices) for the minimap.
   */
  /** A pickup lying on the lawn, bobbing gently. */
  private makePickup(
    p: NonNullable<Simulation['level']['pickups']>[number],
    i: number,
  ): Phaser.GameObjects.Image {
    const img = this.scene.add
      .image((p.x + 0.5) * TILE_SIZE, (p.y + 0.5) * TILE_SIZE, PICKUPS_KEY, PICKUP_FRAME[p.kind])
      // Sorted by row like props: a coin behind a tree or a hive is hidden by it, as it should be.
      .setDepth(this.standingDepth(p.y))
      // A secret yard's treasure stays out of sight until its gate opens.
      .setVisible(!this.yard?.contains(p.x, p.y));
    // Gentle bob so pickups read as collectible, offset per item so they don't move in lockstep.
    this.scene.tweens.add({
      targets: img,
      y: img.y - 1,
      duration: 600,
      yoyo: true,
      repeat: -1,
      delay: (i * 137) % 600,
    });
    return img;
  }

  /**
   * Head to head: the match was rebuilt from the start (a turn came late; docs/HEAD_TO_HEAD.md).
   * Draws the lawn from `sim` as it now is, without replaying any effects: every tile, and the
   * pickups still lying there.
   */
  /** Dots on the minimap besides this mower (a maze match: the middle, the friend), if it has one. */
  setMinimapMarks(marks: readonly MinimapMark[]): void {
    this.minimap?.setMarks(marks);
  }

  resync(sim: Simulation): void {
    this.sim = sim;
    // Every layer that reads the sim moves to the new copy (head to head's turn hints kept the old
    // one and drew a stale turn's path as a big yellow block: owner, build 26).
    this.hints.resync(sim);
    this.darkness?.resync(sim);
    this.minimap?.resync(sim);
    for (let y = 0; y < sim.grid.height; y++) {
      for (let x = 0; x < sim.grid.width; x++) this.redrawTile(x, y);
    }
    (sim.level.pickups ?? []).forEach((p, i) => {
      const img = this.pickups[i];
      if (sim.isCollected(i) && img) {
        this.scene.tweens.killTweensOf(img);
        img.destroy();
        this.pickups[i] = null;
      } else if (!sim.isCollected(i) && !img) {
        this.pickups[i] = this.makePickup(p, i);
      }
    });
  }

  private plantCrowns(): Set<number> {
    const { width, height } = this.sim.grid;
    const shaded = new Set<number>();
    this.art.forEach((id, i) => {
      if (id !== 'bigTree') return;
      const x = i % width;
      const y = Math.floor(i / width);
      for (const [ox, oy] of BIG_TREE_CROWN) {
        const tx = x + ox;
        const ty = y + oy;
        if (tx >= 0 && ty >= 0 && tx < width && ty < height) shaded.add(ty * width + tx);
      }
      const cx = (x + 0.5) * TILE_SIZE;
      const cy = (y + 0.5) * TILE_SIZE;
      // The Meadows' grand trees spread a bigger crown (in flower) and a wider shade.
      const grand = this.biome === 'meadow';
      this.scene.add
        .ellipse(
          cx + 3,
          cy + 4,
          TILE_SIZE * (grand ? 6.2 : 4.6),
          TILE_SIZE * (grand ? 5.6 : 4.2),
          0x0a1a08,
          0.22,
        )
        .setDepth(MOWER_DEPTH - 3);
      const img = this.scene.add
        .image(cx, cy - (grand ? 6 : 2), grand ? GRAND_CROWNS_KEY : CROWNS_KEY, 0)
        .setDepth(MOWER_DEPTH + 3);
      this.crowns.push({ x, y, img });
    });
    return shaded;
  }

  /**
   * Props in a row nearer the camera than the mower draw over it (it passes behind a tree);
   * one right in front of the mower thins out a little so the mower still shows.
   */
  private sortProps(px: number, py: number): void {
    if (this.props.size === 0 && this.blades.size === 0 && this.pickups.length === 0) return;
    const w = this.sim.grid.width;
    const row = Math.floor(py / TILE_SIZE);
    const col = Math.floor(px / TILE_SIZE);
    if (row !== this.propRow) {
      this.propRow = row;
      for (const map of [this.props, this.blades]) {
        for (const [i, img] of map) img.setDepth(this.standingDepth(Math.floor(i / w), row));
      }
      for (const img of this.pickups) {
        img?.setDepth(this.standingDepth(Math.floor(img.y / TILE_SIZE), row));
      }
    }
    for (const map of [this.props, this.blades]) {
      for (const [i, img] of map) {
        const behind = Math.floor(i / w) === row + 1 && Math.abs((i % w) - col) <= 1;
        img.setAlpha(behind ? 0.6 : 1);
      }
    }
  }

  /**
   * Depth of something standing on row `y` (props, tall grass): over the mower when nearer the
   * camera than it, under it otherwise, and nearer rows over farther ones.
   */
  private standingDepth(y: number, mowerRow = this.propRow): number {
    return (y > mowerRow ? MOWER_DEPTH + 0.5 : MOWER_DEPTH - 1.5) + y * 0.001;
  }

  /** Crowns fade while the mower is under them (or about to be), and fill back in after. */
  private fadeCrowns(px: number, py: number): void {
    for (const c of this.crowns) {
      const ox = Math.abs(px / TILE_SIZE - (c.x + 0.5));
      const oy = Math.abs(py / TILE_SIZE - (c.y + 0.5));
      const under = ox < 3.2 && oy < 3.2 && ox + oy < 4.6;
      c.img.setAlpha(c.img.alpha + ((under ? 0.3 : 1) - c.img.alpha) * 0.15);
    }
  }

  /**
   * Clippings out of the mower's discharge (mowerFx.ts): its chute, its reel or its bag. The
   * Fairway Mower's reels each throw ahead of the tile they cut (all three, when streaming).
   */
  private throwClippings(count: number, tileX?: number, tileY?: number): void {
    const id = this.sim.mowerSpec.id as MowerId;
    const dir = this.sim.mower.dir;
    const fx = mowerFx(id, dir);
    if (id === 'fairway') {
      const reels =
        tileX !== undefined && tileY !== undefined
          ? [{ x: (tileX + 0.5) * TILE_SIZE, y: (tileY + 0.5) * TILE_SIZE }]
          : [-1, 0, 1].map((k) => ({
              x: this.mower.x - dy(dir) * k * TILE_SIZE,
              y: this.mower.y + dx(dir) * k * TILE_SIZE,
            }));
      for (const r of reels) {
        this.fx.throwClippings(
          r.x + dx(dir) * 13,
          r.y + dy(dir) * 13,
          fx.aim,
          Math.max(1, Math.round(count / reels.length)),
        );
      }
      return;
    }
    this.fx.throwClippings(
      this.mower.x + fx.discharge.x,
      this.mower.y + fx.discharge.y,
      fx.aim,
      count,
      fx.kind === 'bag',
    );
  }

  /**
   * Bees buzzing about the hive at (x, y): three of them flitting round its dome, each on its own
   * looping path and pace, over everything (they fly).
   */
  private buzz(i: number, x: number, y: number): void {
    const cx = (x + 0.5) * TILE_SIZE;
    const cy = (y + 1) * TILE_SIZE - PROP_LIFT - 11; // the dome's middle
    const bees = [0, 1, 2].map((k) => {
      const bee = this.scene.add.image(cx, cy, FX_KEY, FX_FRAME.bee).setDepth(MOWER_DEPTH + 0.7);
      const rx = 5 + k * 2;
      const ry = 3 + k;
      const t = { a: k * 2.1 };
      this.scene.tweens.add({
        targets: t,
        a: t.a + Math.PI * 2 * (k % 2 === 0 ? 1 : -1),
        duration: 900 + k * 260,
        repeat: -1,
        onUpdate: () => {
          // A wobbly loop: round the dome, with a quick jitter like a bee's.
          const jitter = Math.sin(t.a * 7) * 1.2;
          bee.setPosition(
            Math.round(cx + Math.cos(t.a) * rx + jitter),
            Math.round(cy + Math.sin(t.a * 2) * ry - 2),
          );
          bee.setFlipX(Math.sin(t.a) < 0);
        },
      });
      return bee;
    });
    this.hiveBees.set(i, bees);
  }

  /** Applies the visual consequences of sim events from one tick. */
  applyEvents(events: readonly SimEvent[]): void {
    this.hints.applyEvents(events);
    if (events.length > 0) this.minimap?.invalidate();
    for (const e of events) {
      switch (e.type) {
        case 'tileMowed': {
          this.redrawTile(e.x, e.y);
          this.artFeedback(e.x, e.y);
          if (e.fresh || e.result === 'thinned' || e.result === 'cleared') {
            // A burst out of the mower's discharge, then a stream while it keeps cutting.
            const heavy = e.result === 'thinned' || e.result === 'cleared';
            this.throwClippings(heavy ? 14 : 8, e.x, e.y);
            this.cutUntil = this.scene.time.now + 280;
            this.cutHeavy = heavy;
          }
          if (e.fresh) this.fx.wheelTracks(e.x, e.y, dy(this.sim.mower.dir) !== 0);
          break;
        }
        case 'crashed':
          if (e.critter === 'dog') {
            this.floatText(this.mower.x, this.mower.y - 8, `-${ECONOMY.dogBumpCost}`);
          }
          // A lawn item a stronger mower would have smashed: say so, so the tiers can be learned.
          if (e.tooTough) this.floatText(this.mower.x, this.mower.y - 10, 'Too tough!');
          // The Bumper took it: no hit lost.
          else if (e.bumper) this.floatText(this.mower.x, this.mower.y - 10, 'BUMPER!');
          this.fx.shake(140, 0.012);
          this.fx.puff(this.mower.x + dx(e.dir) * 8, this.mower.y + dy(e.dir) * 8);
          this.blinkMower();
          break;
        case 'reserveCan':
          // The tank ran dry and the Reserve Can filled it.
          this.floatText(this.mower.x, this.mower.y - 10, 'RESERVE CAN!');
          break;
        case 'mowerBroken':
          this.mower.setFrame(MOWER_BROKEN_FRAME);
          this.fx.shake(260, 0.02);
          this.fx.breakApart(this.mower.x, this.mower.y);
          this.scene.tweens.add({
            targets: this.mower,
            y: this.mower.y - 4,
            duration: 120,
            yoyo: true,
          });
          break;
        case 'levelComplete':
          this.fx.celebrate(this.mapWidthPx, this.mapHeightPx);
          break;
        case 'boostUsed':
          this.fx.sparkle(this.sim.mower.x, this.sim.mower.y);
          this.floatText(this.mower.x, this.mower.y - 10, BOOST_BY_ID[e.boost].name.toUpperCase());
          break;
        case 'moleWarning':
          this.tremble(e.x, e.y);
          break;
        case 'molehill':
          this.redrawTile(e.x, e.y);
          this.fx.puff((e.x + 0.5) * TILE_SIZE, (e.y + 0.5) * TILE_SIZE);
          this.molePeek(e.x, e.y);
          break;
        case 'rivalMowed': {
          this.redrawTile(e.x, e.y);
          this.rivalWash
            ?.fillStyle(0x7cc6ff, 0.18)
            .fillRect(e.x * TILE_SIZE, e.y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
          const heading = (Math.atan2(dy(e.dir), dx(e.dir)) * 180) / Math.PI;
          this.fx.sprayClippings(e.x, e.y, heading, false);
          break;
        }
        case 'pickup': {
          const img = this.pickups[e.index];
          if (e.pulled && img) {
            this.pullCoin(img, e.index);
            break;
          }
          this.fx.sparkle(e.x, e.y);
          if (e.spare) this.floatText((e.x + 0.5) * TILE_SIZE, e.y * TILE_SIZE, '+1');
          if (e.kind === 'hoard') this.floatText((e.x + 0.5) * TILE_SIZE, e.y * TILE_SIZE, '+25');
          if (img) {
            this.pickups[e.index] = null;
            this.scene.tweens.killTweensOf(img);
            this.scene.tweens.add({
              targets: img,
              y: img.y - 10,
              alpha: 0,
              duration: 260,
              onComplete: () => img.destroy(),
            });
          }
          break;
        }
        case 'toolUsed': {
          for (const p of e.cleared) {
            // A sprinkler the key turned off is closed, not smashed.
            const i = p.y * this.sim.grid.width + p.x;
            if (this.art[i] === 'sprinkler') this.sprinklersOff.add(i);
            this.redrawTile(p.x, p.y);
          }
          if (e.shooed.length > 0 || e.cleared.length === 0) this.ring();
          break;
        }
        case 'toolDenied':
          break;
        case 'critterShooed': {
          const s = this.critterSprites[e.index];
          if (s) this.exclaim(s.img.x, s.img.y);
          break;
        }
        case 'critterPoofed': {
          // Bumped: gone in a white puff (back home after a while).
          const s = this.critterSprites[e.index];
          if (s) {
            this.scene.tweens.killTweensOf(s.img);
            s.img.setAlpha(0);
          }
          this.fx.poof((e.x + 0.5) * TILE_SIZE, (e.y + 0.5) * TILE_SIZE);
          break;
        }
        case 'outOfFuel':
          this.mower.setAlpha(0.6);
          break;
        case 'flatTire':
          if (e.saved) {
            this.floatText((e.x + 0.5) * TILE_SIZE, e.y * TILE_SIZE, 'TOUGH TIRES!');
          } else {
            this.floatText((e.x + 0.5) * TILE_SIZE, e.y * TILE_SIZE, 'FLAT!');
            this.fx.puff(this.mower.x, this.mower.y + 4);
            this.fx.shake(120, 0.006);
          }
          break;
        case 'tirePatched':
          this.floatText((e.x + 0.5) * TILE_SIZE, e.y * TILE_SIZE - 6, 'PATCHED');
          break;
        case 'treeCreak':
          this.creak(e.index);
          break;
        case 'ballWarning':
          break;
        case 'kicked':
          // A thump of turf where the mower's nose met the ball.
          this.fx.puff((e.x + 0.5) * TILE_SIZE, (e.y + 0.5) * TILE_SIZE + 3);
          break;
        case 'goal':
          this.scoreGoal(e.x, e.y);
          break;
        case 'ballReset':
          this.ballPoppedAt = this.scene.time.now;
          this.fx.sparkle(e.x, e.y);
          break;
        case 'ballHit':
          this.floatText((e.x + 0.5) * TILE_SIZE, e.y * TILE_SIZE, 'BOING!');
          this.fx.shake(140, 0.012);
          this.blinkMower();
          break;
        case 'ballGone':
          this.fx.puff((e.x + 0.5) * TILE_SIZE, (e.y + 0.5) * TILE_SIZE);
          break;
        case 'treeFell':
          this.treeFell(e);
          break;
        case 'revived':
          // Back in running order: whole again, with a little sparkle.
          this.mower.setAlpha(1).setFrame(this.sim.mower.dir);
          this.fx.sparkle(e.x, e.y);
          this.scene.tweens.add({
            targets: this.mower,
            y: this.mower.y - 4,
            duration: 120,
            yoyo: true,
          });
          break;
        case 'gateOpened':
          // The way into the secret yard: the gate bursts, the yard and its treasure appear.
          this.fx.breakApart((e.x + 0.5) * TILE_SIZE, (e.y + 0.5) * TILE_SIZE);
          this.fx.shake(160, 0.01);
          this.yard?.open();
          for (const img of this.pickups) img?.setVisible(true);
          break;
        case 'smashed':
          // Damaged property: the decoration bursts apart.
          this.smashedTiles.add(e.y * this.sim.grid.width + e.x);
          this.redrawTile(e.x, e.y);
          this.fx.breakApart((e.x + 0.5) * TILE_SIZE, (e.y + 0.5) * TILE_SIZE);
          this.fx.shake(120 + e.tier * 30, 0.008 + e.tier * 0.002);
          this.floatText((e.x + 0.5) * TILE_SIZE, e.y * TILE_SIZE, `-${smashCostFor(e.tier)}`);
          break;
        case 'hiveSmashed':
          // A beehive smashed: the swarm boils out after the mower, stinging (a heart, unless
          // the shield is up), and the beekeeper's loss.
          this.smashedTiles.add(e.y * this.sim.grid.width + e.x);
          this.redrawTile(e.x, e.y);
          this.fx.breakApart((e.x + 0.5) * TILE_SIZE, (e.y + 0.5) * TILE_SIZE);
          this.fx.swarm(this.mower, 1600);
          this.floatText((e.x + 0.5) * TILE_SIZE, e.y * TILE_SIZE, `-${ECONOMY.hiveCost}`);
          if (e.stung) {
            this.fx.shake(220, 0.014);
            this.blinkMower();
            this.floatText(this.mower.x, this.mower.y - 12, 'Stung!');
          }
          break;
        case 'regrew':
          // A meadow patch left half mowed springs back: the grass returns with a green flurry.
          for (const p of e.tiles) {
            this.redrawTile(p.x, p.y);
            this.fx.throwClippings((p.x + 0.5) * TILE_SIZE, (p.y + 0.8) * TILE_SIZE, 270, 2, true);
          }
          break;
        case 'sprinklerBroken':
          // Damaged property: the head snaps off.
          this.redrawTile(e.x, e.y);
          this.fx.breakApart((e.x + 0.5) * TILE_SIZE, (e.y + 0.5) * TILE_SIZE);
          this.fx.shake(120, 0.008);
          this.floatText((e.x + 0.5) * TILE_SIZE, e.y * TILE_SIZE, `-${ECONOMY.sprinklerCost}`);
          break;
        case 'chewed':
          // Snag: the hose gets chewed and leaks a little.
          this.redrawTile(e.x, e.y);
          this.fx.puff((e.x + 0.5) * TILE_SIZE, (e.y + 0.5) * TILE_SIZE);
          this.fx.shake(100, 0.006);
          this.floatText((e.x + 0.5) * TILE_SIZE, e.y * TILE_SIZE, `-${ECONOMY.hoseCost}`);
          break;
        case 'trampled':
          this.trampled.add(e.y * this.sim.grid.width + e.x);
          this.redrawTile(e.x, e.y);
          this.fx.trample(e.x, e.y);
          break;
        case 'turned':
        case 'turnRejected':
          break;
      }
    }
  }

  /**
   * Lawn life (docs/ART_DIRECTION.md: subtle and slow): the uncut grass sways in a light breeze,
   * soft cloud shadows drift across, and now and then a butterfly flutters along the edge.
   * Off with reduced motion. Purely visual; never touches the sim.
   */
  private startLawnLife(): void {
    const scene = this.scene;
    let beat = 0;
    const sway = scene.time.addEvent({
      delay: 700,
      loop: true,
      callback: () => {
        beat++;
        // Each frame on its own rhythm so the lawn ripples rather than ticks in unison; long
        // grass catches the breeze a beat later than short.
        const lean: Record<SwayingFrame, boolean> = {
          grass: beat % 2 === 0,
          grass2: beat % 3 === 0,
          grass3: beat % 2 === 1,
          tallGrass: beat % 2 === 1,
          tallGrassCut: beat % 4 === 1,
          wheat: (beat + 1) % 3 === 0,
          meadowGrass: beat % 3 === 1,
        };
        swayGrass(scene, (frame) => lean[frame]);
        swayBlades(scene, { tall: lean.tallGrass, rough: lean.tallGrassCut });
      },
    });
    // Water ripples drift along, a pixel at a time.
    let ripple = 0;
    const water = scene.time.addEvent({
      delay: 260,
      loop: true,
      callback: () => rippleWater(scene, ++ripple),
    });
    // Two big, very faint cloud shadows drifting slowly right, wrapping around.
    const w = this.mapWidthPx;
    const h = this.mapHeightPx;
    const clouds = [0, 1].map((i) => {
      const c = scene.add
        .ellipse(i === 0 ? w * 0.2 : w * 0.9, h * (i === 0 ? 0.3 : 0.75), 90, 52, 0x0a1a08, 0.1)
        .setDepth(MOWER_DEPTH - 3);
      scene.tweens.add({
        targets: c,
        x: { from: -60, to: w + 60 },
        duration: 38000 + i * 9000,
        delay: -i * 17000,
        repeat: -1,
      });
      return c;
    });
    // A butterfly that visits now and then.
    const fly = scene.add
      .graphics()
      .setDepth(MOWER_DEPTH + 1)
      .setVisible(false);
    const flutter = scene.time.addEvent({
      delay: 9000,
      loop: true,
      callback: () => this.butterfly(fly),
    });
    scene.events.once('shutdown', () => {
      sway.remove();
      flutter.remove();
      water.remove();
      swayGrass(scene, () => false);
      swayBlades(scene, { tall: false, rough: false });
      rippleWater(scene, 0);
      for (const c of clouds) c.destroy();
    });
  }

  /** One butterfly flight along a random edge of the lawn, wings flapping. */
  private butterfly(fly: Phaser.GameObjects.Graphics): void {
    const scene = this.scene;
    const w = this.mapWidthPx;
    const h = this.mapHeightPx;
    const color = [0xffe066, 0xffffff, 0xff9ec4][Math.floor(Math.random() * 3)]!;
    let open = true;
    const draw = () => {
      fly.clear();
      fly.fillStyle(0x10180c).fillRect(-1, -1, 1, 3);
      fly.fillStyle(color);
      if (open) fly.fillRect(-4, -2, 3, 3).fillRect(1, -2, 3, 3);
      else fly.fillRect(-2, -2, 1, 3).fillRect(1, -2, 1, 3);
    };
    const flap = scene.time.addEvent({
      delay: 110,
      loop: true,
      callback: () => ((open = !open), draw()),
    });
    draw();
    const fromLeft = Math.random() < 0.5;
    const y0 = Math.random() * h;
    fly.setPosition(fromLeft ? -10 : w + 10, y0).setVisible(true);
    scene.tweens.add({
      targets: fly,
      x: fromLeft ? w + 10 : -10,
      y: { value: y0 + (Math.random() - 0.5) * 60, ease: 'Sine.easeInOut' },
      duration: 7000,
      onComplete: () => {
        flap.remove();
        fly.setVisible(false);
      },
    });
  }

  /**
   * Sprinkler spray, from the sim's timing: a fan of droplets sweeping round the head and a wet
   * sheen on the tiles it soaks (slippery: no turning there); drips just before it starts.
   */
  private drawSpray(): void {
    const g = this.spray.clear();
    const now = this.scene.time.now;
    this.drawHoseWater(g, now);
    if (this.sprinklers.length === 0) return;
    for (const s of this.sprinklers) {
      const state = this.sim.sprinklerState(s.x, s.y);
      const cx = (s.x + 0.5) * TILE_SIZE;
      const cy = (s.y + 0.5) * TILE_SIZE;
      if (state === 'warn') {
        // A drip or two: about to start.
        if (Math.floor(now / 160) % 2 === 0) g.fillStyle(0xbfe6ff).fillRect(cx - 1, cy - 7, 2, 2);
        continue;
      }
      if (state !== 'on') continue;
      // Wet tiles.
      for (let oy = -1; oy <= 1; oy++) {
        for (let ox = -1; ox <= 1; ox++) {
          if (ox === 0 && oy === 0) continue;
          const tx = (s.x + ox) * TILE_SIZE;
          const ty = (s.y + oy) * TILE_SIZE;
          if (!this.sim.grid.inBounds(s.x + ox, s.y + oy)) continue;
          g.fillStyle(0x5aa9e6, 0.22).fillRect(tx, ty, TILE_SIZE, TILE_SIZE);
          if ((Math.floor(now / 200) + ox * 3 + oy * 5) % 4 === 0) {
            g.fillStyle(0xffffff, 0.7).fillRect(tx + 4 + ox, ty + 6 - oy, 1, 1);
          }
        }
      }
      // The fan of droplets, sweeping round.
      const sweep = (now / 900) * Math.PI * 2;
      for (let k = 0; k < 14; k++) {
        const a = sweep + (k % 7) * 0.12 - 0.36;
        const r = 5 + ((k * 13 + Math.floor(now / 40)) % 18);
        g.fillStyle(k % 3 === 0 ? 0xffffff : 0x9fd6ff, 0.9).fillRect(
          Math.round(cx + Math.cos(a) * r),
          Math.round(cy + Math.sin(a) * r),
          1,
          1,
        );
      }
    }
  }

  /**
   * Boss lawn art: on every tile still to get right, two short ticks show the stripe it wants
   * (side to side, or up and down): faint on unmowed grass, red on a tile mowed the wrong way.
   * Tiles that match show nothing, so the picture emerges clean.
   */
  /**
   * Lawn Art (a picture on a show lawn): a chalk line round the picture, dashed like a pitch
   * marking, so its shape shows before a blade is cut.
   */
  private drawChalkOutline(): void {
    const art = this.sim.level.art!;
    const { width, height } = this.sim.grid;
    const g = this.scene.add.graphics().setDepth(MOWER_DEPTH - 3);
    const inPicture = (x: number, y: number) =>
      x >= 0 && y >= 0 && x < width && y < height && (art.pattern[y * width + x] ?? 0) !== 0;
    g.fillStyle(0xf4f1e6, 0.75);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (!inPicture(x, y)) continue;
        const px = x * TILE_SIZE;
        const py = y * TILE_SIZE;
        for (let k = 1; k < TILE_SIZE; k += 4) {
          if (!inPicture(x, y - 1)) g.fillRect(px + k, py, 2, 1);
          if (!inPicture(x, y + 1)) g.fillRect(px + k, py + TILE_SIZE - 1, 2, 1);
          if (!inPicture(x - 1, y)) g.fillRect(px, py + k, 1, 2);
          if (!inPicture(x + 1, y)) g.fillRect(px + TILE_SIZE - 1, py + k, 1, 2);
        }
      }
    }
  }

  /** Lawn Art: a sparkle for a picture tile mowed the way it should be, a red flash for one not. */
  private artFeedback(x: number, y: number): void {
    const art = this.sim.level.art;
    if (!art) return;
    const want = art.pattern[y * this.sim.grid.width + x] ?? 0;
    if (want === 0) return;
    const cx = (x + 0.5) * TILE_SIZE;
    const cy = (y + 0.5) * TILE_SIZE;
    if (this.sim.artAxisAt(x, y) === want) {
      this.fx.sparkle(x, y);
      return;
    }
    const flash = this.scene.add.graphics().setDepth(MOWER_DEPTH + 1);
    flash.lineStyle(2, 0xff5a4a, 0.9).strokeRect(cx - 7, cy - 7, 14, 14);
    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      delay: 250,
      duration: 500,
      onComplete: () => flash.destroy(),
    });
  }

  private drawArtMarks(): void {
    const g = this.artMarks;
    const art = this.sim.level.art;
    if (!g || !art) return;
    g.clear();
    const { width, height } = this.sim.grid;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const want = art.pattern[y * width + x] ?? 0;
        if (want === 0) continue;
        const have = this.sim.artAxisAt(x, y);
        if (have === want) continue;
        g.fillStyle(have === 0 ? 0xffffff : 0xff5a4a, have === 0 ? 0.35 : 0.8);
        const px = x * TILE_SIZE;
        const py = y * TILE_SIZE;
        if (want === 1) {
          g.fillRect(px + 4, py + 6, 8, 1).fillRect(px + 4, py + 9, 8, 1);
        } else {
          g.fillRect(px + 6, py + 4, 1, 8).fillRect(px + 9, py + 4, 1, 8);
        }
      }
    }
  }

  /**
   * Boss storm: a dark wash over every row the rain has reached, slanting rain streaks falling
   * through it, and a churning front edge (with spray just ahead of it).
   */
  private drawRain(): void {
    const g = this.rain;
    if (!g) return;
    g.clear();
    const row = this.sim.stormRow();
    if (row === null || row >= this.sim.grid.height) return;
    const w = this.mapWidthPx;
    const top = row * TILE_SIZE;
    const bottom = this.mapHeightPx + 64;
    const now = this.scene.time.now;
    g.fillStyle(0x1c2a3a, 0.62).fillRect(-32, top, w + 64, bottom - top);
    // Streaks: short slanted lines on a scrolling grid.
    g.fillStyle(0xa9c8e8, 0.8);
    const fall = Math.floor(now / 22);
    for (let y = top; y < bottom; y += 9) {
      for (let x = -32; x < w + 32; x += 7) {
        const k = (x * 7 + y * 3) % 11;
        if (k > 4) continue;
        const sy = top + ((y - top + fall + k * 5) % (bottom - top));
        const sx = x + ((fall + k) % 7);
        g.fillRect(sx, sy, 1, 3);
        g.fillRect(sx - 1, sy + 3, 1, 1);
      }
    }
    // The front: a bright, wavy edge and a little spray just ahead.
    for (let x = -32; x < w + 32; x += 2) {
      const wave = Math.round(Math.sin(x / 9 + now / 260) * 2);
      g.fillStyle(0xdbe8f5, 0.85).fillRect(x, top + wave - 1, 2, 2);
      if ((x + Math.floor(now / 80)) % 10 === 0)
        g.fillStyle(0xdbe8f5, 0.6).fillRect(x, top + wave - 5, 1, 1);
    }
  }

  /**
   * Stray balls: while the whistle blows, a blinking arrow at the edge and a dotted line down the
   * lane it will take; then the ball itself, rolling (its patches turn as it goes).
   */
  private drawBall(alpha: number): void {
    const g = this.ballFx;
    if (!g) return;
    g.clear();
    const b = this.sim.ball;
    if (!b) return;
    const T = TILE_SIZE;
    const now = this.scene.time.now;
    const ddx = dx(b.dir);
    const ddy = dy(b.dir);
    if (b.warn > 0) {
      // The whole lane glows, pulsing, so it shows wherever the camera is.
      const run = this.sim.ballRun(b.x, b.y, b.dir);
      const pulse = 0.1 + 0.08 * Math.sin(now / 90);
      g.fillStyle(0xfff3d6, pulse);
      for (const p of run) g.fillRect(p.x * T, p.y * T, T, T);
      g.fillStyle(0xffffff, 0.6);
      for (const p of run) g.fillRect((p.x + 0.5) * T - 1, (p.y + 0.5) * T - 1, 2, 2);
      if (Math.floor(now / 160) % 2 === 0) {
        // The arrow on the start tile, pointing the way the ball will come; kept on screen at the
        // edge of the view when the start is off it (wide lawns).
        const view = this.scene.cameras.main.worldView;
        const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
        const cx = clamp((b.x + 0.5) * T, view.x + 9, view.right - 9);
        const cy = clamp((b.y + 0.5) * T, view.y + 9, view.bottom - 9);
        g.fillStyle(0x10180c);
        g.fillTriangle(
          cx + ddx * 7,
          cy + ddy * 7,
          cx - ddx * 5 + ddy * 6,
          cy - ddy * 5 + ddx * 6,
          cx - ddx * 5 - ddy * 6,
          cy - ddy * 5 - ddx * 6,
        );
        g.fillStyle(0xff8a1f);
        g.fillTriangle(
          cx + ddx * 5,
          cy + ddy * 5,
          cx - ddx * 4 + ddy * 4,
          cy - ddy * 4 + ddx * 4,
          cx - ddx * 4 - ddy * 4,
          cy - ddy * 4 - ddx * 4,
        );
      }
      return;
    }
    const t = (b.progress + alpha) / this.sim.ballTicks();
    const px = Math.round((b.x + 0.5 + ddx * t) * T);
    const py = Math.round((b.y + 0.5 + ddy * t) * T);
    g.fillStyle(0x0a1a08, 0.35).fillEllipse(px + 1, py + 3, 9, 4);
    g.fillStyle(0x10180c).fillCircle(px, py, 4.5);
    g.fillStyle(0xffffff).fillCircle(px, py, 3.5);
    // Two patches that roll round as it goes.
    const spin = Math.floor((b.x + b.y + t) * 4) % 4;
    const spots: [number, number][] = [
      [-2, -1],
      [1, -2],
      [2, 1],
      [-1, 2],
    ];
    g.fillStyle(0x222326);
    for (const k of [spin, (spin + 2) % 4]) {
      const [sx, sy] = spots[k]!;
      g.fillRect(px + sx - 1, py + sy - 1, 2, 2);
    }
  }

  /**
   * A live race: the friend reached this coin first, so it vanishes from this lawn in a puff, the
   * friend's color. (Its pickup in this sim later shows nothing.)
   */
  friendTook(index: number): void {
    const img = this.pickups[index];
    if (!img) return;
    this.pickups[index] = null;
    this.scene.tweens.killTweensOf(img);
    img.setTint(0xffd8a8);
    this.scene.tweens.add({
      targets: img,
      scale: 0.2,
      alpha: 0,
      duration: 320,
      onComplete: () => img.destroy(),
    });
  }

  /** A little label that rises and fades from a lawn spot (a spare pickup's coin). */
  private floatText(x: number, y: number, text: string): void {
    const t = label(this.scene, x, y, text, 1, UI.accent)
      .setOrigin(0.5, 1)
      .setDepth(MOWER_DEPTH + 5);
    t.setX(Math.round(t.x - (t.width % 2) / 2));
    this.scene.tweens.add({
      targets: t,
      y: y - 12,
      alpha: 0,
      duration: 700,
      ease: 'Quad.easeOut',
      onComplete: () => t.destroy(),
    });
  }

  /** Shows a turn that is not in the sim yet (picked during the resume countdown). */
  previewTurn(intent: SteerIntent | null): void {
    this.hints.previewIntent = intent;
  }

  private lastExhaust = 0;
  /** Clippings stream from the discharge until then (ms), after each fresh cut. */
  private cutUntil = 0;
  private cutHeavy = false;
  private lastStream = 0;

  // ── Admiring the finished lawn ───────────────────────────────────────────────────────────

  /**
   * Where the camera's center may go: the whole lawn and its margin (a lawn smaller than the
   * view stays centered).
   */
  private admireBounds(): { x0: number; x1: number; y0: number; y1: number } {
    const cam = this.scene.cameras.main;
    const span = (map: number, edge: number, view: number): [number, number] =>
      map + 2 * edge <= view ? [map / 2, map / 2] : [view / 2 - edge, map + edge - view / 2];
    const m = this.margins();
    const [x0, x1] = span(this.mapWidthPx + m.l + m.r, 0, cam.width).map((v) => v - m.l) as [
      number,
      number,
    ];
    const [y0, y1] = span(this.mapHeightPx + m.t + m.b, 0, cam.height).map((v) => v - m.t) as [
      number,
      number,
    ];
    return { x0, x1, y0, y1 };
  }

  /**
   * How far past each edge of the lawn the camera may look: its fence and a strip of the world,
   * a whole tile on a secret yard's side (so its gate shows), and the yard itself once it is open.
   */
  private margins(): { l: number; r: number; t: number; b: number } {
    const m = { l: EDGE_X, r: EDGE_X, t: EDGE_Y, b: EDGE_Y };
    // Mower soccer: room for the top goal's net, reaching back past the fence.
    if (this.sim.level.soccer) m.t = Math.max(m.t, GOAL_POST + GOAL_NET + 3);
    const yard = this.yard;
    if (!yard) return m;
    const reach = yard.opened ? (YARD_DEPTH + 1) * TILE_SIZE + 6 : TILE_SIZE + 6;
    const key = { top: 't', bottom: 'b', left: 'l', right: 'r' }[yard.yard.side] as keyof typeof m;
    m[key] = Math.max(m[key], reach);
    return m;
  }

  /** Hands the camera over to admiring (the mower no longer pulls it along). */
  beginAdmire(): void {
    this.admiring = true;
  }

  /** Moves the admiring camera's center to (x, y), kept over the lawn. */
  admireTo(x: number, y: number): void {
    const b = this.admireBounds();
    this.camCenter = {
      x: Math.max(b.x0, Math.min(b.x1, x)),
      y: Math.max(b.y0, Math.min(b.y1, y)),
    };
  }

  /** The admiring camera's center. */
  admireCenter(): { x: number; y: number } {
    const cam = this.scene.cameras.main;
    return this.camCenter ?? { x: cam.scrollX + cam.width / 2, y: cam.scrollY + cam.height / 2 };
  }

  /**
   * A look over the finished lawn (owner, 2026-10-05: admire it, like a dad after mowing): the
   * camera eases from the top of the lawn to the bottom (and across a wide one) at full size, so
   * the stripes stay crisp, while a shine sweeps the stripes. `onDone` when it has finished.
   * Lawns that fit the screen just get the shine.
   */
  flyover(onDone: () => void): Phaser.Tweens.Tween | null {
    this.beginAdmire();
    const b = this.admireBounds();
    this.shine();
    const from = this.admireCenter();
    const travel = Math.abs(b.y1 - b.y0) + Math.abs(b.x1 - b.x0);
    if (travel < 8) {
      this.scene.time.delayedCall(1100, onDone);
      return null;
    }
    // Up to the top, then down the lawn (across it on the way, for a wide one).
    const start = { x: b.x0, y: b.y0 };
    const k = { t: 0 };
    const duration = Math.min(2600, 900 + travel * 4);
    return this.scene.tweens.add({
      targets: k,
      t: 1,
      duration,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        const t = k.t;
        // The first fifth eases up to the top-left; the rest sweeps down to the bottom-right.
        if (t < 0.2) {
          const u = t / 0.2;
          this.admireTo(from.x + (start.x - from.x) * u, from.y + (start.y - from.y) * u);
        } else {
          const u = (t - 0.2) / 0.8;
          this.admireTo(b.x0 + (b.x1 - b.x0) * u, b.y0 + (b.y1 - b.y0) * u);
        }
      },
      onComplete: () => onDone(),
    });
  }

  /**
   * A shine across the fresh stripes: a bright diagonal band sweeps the lawn, and sparkles wink
   * on mowed tiles.
   */
  shine(): void {
    const scene = this.scene;
    const w = this.mapWidthPx;
    const h = this.mapHeightPx;
    const band = scene.add.graphics().setDepth(DEPTH_SHINE).setBlendMode(Phaser.BlendModes.ADD);
    const sweep = { x: -h - 24 };
    scene.tweens.add({
      targets: sweep,
      x: w + 24,
      duration: 1500,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        band.clear();
        // A slanted band (rise over run 1:1), a soft edge each side.
        for (const [off, a] of [
          [0, 0.22],
          [-6, 0.1],
          [6, 0.1],
        ] as const) {
          band.fillStyle(0xffffff, a);
          for (let y = 0; y < h; y += 2) {
            const x = sweep.x + y + off;
            if (x > -8 && x < w) band.fillRect(Math.max(0, x), y, 6, 2);
          }
        }
      },
      onComplete: () => band.destroy(),
    });
    const grid = this.sim.grid;
    const spots: [number, number][] = [];
    for (let y = 0; y < grid.height; y++) {
      for (let x = 0; x < grid.width; x++) if (grid.tileAt(x, y) === Tile.Mowed) spots.push([x, y]);
    }
    const count = Math.min(18, Math.ceil(spots.length / 6));
    for (let i = 0; i < count && spots.length > 0; i++) {
      const [tx, ty] = spots.splice(Math.floor(Math.random() * spots.length), 1)[0]!;
      const s = scene.add
        .graphics()
        .setDepth(DEPTH_SHINE)
        .setPosition(
          tx * TILE_SIZE + 4 + Math.random() * 8,
          ty * TILE_SIZE + 4 + Math.random() * 8,
        );
      s.fillStyle(0xffffff).fillRect(-1, 0, 3, 1).fillRect(0, -1, 1, 3);
      s.setScale(0).setAlpha(0.9);
      scene.tweens.add({
        targets: s,
        scale: { from: 0, to: 1.5 },
        alpha: { from: 1, to: 0 },
        delay: 200 + i * 70,
        duration: 520,
        ease: 'Quad.easeOut',
        onComplete: () => s.destroy(),
      });
    }
  }

  /**
   * The coin magnet drew this coin in: it lifts off its tile and flies to the mower, speeding up as
   * it goes and following the mower as it drives, then winks out in a few sparkles.
   */
  private pullCoin(img: Phaser.GameObjects.Image, index: number): void {
    this.pickups[index] = null;
    this.scene.tweens.killTweensOf(img);
    const from = { x: img.x, y: img.y };
    img.setDepth(MOWER_DEPTH + 1);
    const flight = { t: 0 };
    this.scene.tweens.add({
      targets: flight,
      t: 1,
      duration: 320,
      ease: 'Quad.easeIn',
      onUpdate: () => {
        const to = this.mower;
        // A little arc: up and over, landing on the mower.
        const lift = Math.sin(flight.t * Math.PI) * 6;
        img.setPosition(
          from.x + (to.x - from.x) * flight.t,
          from.y + (to.y - from.y) * flight.t - lift,
        );
        img.setScale(1 - 0.4 * flight.t);
      },
      onComplete: () => {
        this.fx.sparkleAt(this.mower.x, this.mower.y);
        img.destroy();
      },
    });
  }

  /** Positions the mower and camera for this frame. `alpha` = progress toward the next tick. */
  render(alpha: number, live = true): void {
    const m = this.sim.mower;
    const parked = m.progress === 0 && (m.parked || m.parkRequested);
    // Head to head: dizzy from a bump in the side, it spins where it stands.
    const dizzy = this.sim.dizzy > 0;
    const willAdvance =
      this.sim.status === 'running' &&
      !parked &&
      !dizzy &&
      !this.sim.blocks(m.x + dx(m.dir), m.y + dy(m.dir));
    const { px, py } = mowerPixelPosition(m, this.sim.crossTicks(), dizzy ? 0 : alpha, willAdvance);
    // A gas engine rumbles: the mower shivers a pixel (slower while it idles, parked).
    const engineOn =
      live && this.sim.status === 'running' && this.sim.mowerSpec.fuelTiles !== undefined;
    const rumble = engineOn ? Math.floor(this.scene.time.now / (parked ? 140 : 70)) % 2 : 0;
    this.mower.setPosition(px, py - rumble);
    // Safe after a crash: the mower blinks for as long as it lasts (owner, 2026-10-07), so a
    // second knock in that time reads as harmless rather than as a lucky bug.
    if (this.sim.status === 'running' && m.invulnTicks > 0) {
      this.mower.setAlpha(Math.floor(this.scene.time.now / 70) % 2 ? 0.3 : 1);
      this.blinking = true;
    } else if (this.blinking) {
      this.mower.setAlpha(1);
      this.blinking = false;
    }
    const r = this.sim.rival;
    if (this.rival && r) {
      const at = mowerPixelPosition(r, this.sim.rivalCrossTicks(), alpha, r.progress > 0);
      this.rival.setPosition(at.px, at.py).setFrame(r.dir);
    }
    this.drawSpray();
    this.drawBall(alpha);
    this.drawPitchBall(alpha);
    this.drawRain();
    this.drawArtMarks();
    this.drawBoosts(px, py, live);
    // Gas mowers puff exhaust from their engine while running.
    const now = this.scene.time.now;
    const fxAt = mowerFx(this.sim.mowerSpec.id as MowerId, this.sim.mower.dir);
    if (engineOn && fxAt.exhaust && now - this.lastExhaust > 380) {
      this.lastExhaust = now;
      this.fx.exhaust(px + fxAt.exhaust.x, py + fxAt.exhaust.y);
    }
    // Still cutting: a steady stream of clippings out of the discharge.
    if (
      live &&
      this.sim.status === 'running' &&
      now < this.cutUntil &&
      now - this.lastStream > 50
    ) {
      this.lastStream = now;
      this.throwClippings(this.cutHeavy ? 4 : 3);
    }
    if (this.sim.status !== 'broken') {
      // Parked: spinning on the spot, a quarter turn every few frames (dizzy: a little faster).
      const spin = dizzy
        ? Math.floor(this.scene.time.now / DIZZY_SPIN_MS) % 4
        : parked && m.parked
          ? Math.floor(this.scene.time.now / 90) % 4
          : 0;
      this.mower.setFrame((m.dir + spin) % 4);
    }
    this.mowerOutline?.follow(this.mower);
    this.hints.render(px, py);
    this.syncCritters();

    // Always 1x: every lawn uses the same tile size, so bigger lawns look bigger (owner, lm-d4m.1).
    const cam = this.scene.cameras.main;
    const edge = this.margins();
    const target = {
      // (A margin past the lawn's edges, so its fence stays in view on lawns bigger than the screen.)
      x: cameraCenter(this.mapWidthPx + edge.l + edge.r, cam.width, px + edge.l) - edge.l,
      y:
        cameraCenter(this.mapHeightPx + edge.t + edge.b, cam.height, py + edge.t, FOLLOW_ANCHOR) -
        edge.t,
    };
    const c = this.camCenter ?? target;
    if (!this.admiring) {
      c.x += (target.x - c.x) * CAMERA_LERP;
      c.y += (target.y - c.y) * CAMERA_LERP;
    }
    this.camCenter = c;
    // Whole-pixel scroll: a fractional one makes bobbing pickups and sprites shimmer.
    cam.setScroll(Math.round(c.x - cam.width / 2), Math.round(c.y - cam.height / 2));
    this.fadeCrowns(px, py);
    this.sortProps(px, py);
    this.minimap?.render(cam, px, py, now);
    this.darkness?.render(px, py, now);
  }

  /** Hops critter sprites to their sim tiles with a little arc. */
  private syncCritters(): void {
    this.sim.critters.list.forEach((c, i) => {
      const s = this.critterSprites[i];
      if (!s || s.hopTick === c.lastHopTick) return;
      s.hopTick = c.lastHopTick;
      const tx = (c.x + 0.5) * TILE_SIZE;
      const ty = (c.y + 0.5) * TILE_SIZE;
      this.scene.tweens.killTweensOf(s.img);
      if (c.gone) {
        // The stag leaves the field: one more stride off the edge, fading as it goes.
        const ox = c.x === 0 ? -1 : c.x === this.sim.grid.width - 1 ? 1 : 0;
        const oy = ox === 0 ? (c.y === 0 ? -1 : 1) : 0;
        if (ox !== 0) s.img.setFlipX(ox < 0);
        this.scene.tweens.add({
          targets: s.img,
          x: tx + ox * TILE_SIZE * 2,
          y: ty + oy * TILE_SIZE * 2,
          alpha: 0,
          duration: 600,
        });
        return;
      }
      if (c.awayTicks > 0) {
        // Sent home by the horn: a hop and gone.
        this.scene.tweens.add({ targets: s.img, y: s.img.y - 8, alpha: 0, duration: 250 });
        return;
      }
      if (c.inside) {
        // The dog goes in: up into the doorway and out of sight.
        this.scene.tweens.add({ targets: s.img, y: ty - 8, alpha: 0, duration: 200 });
        return;
      }
      if (s.img.alpha < 1) {
        // Out of the doorway onto the lawn.
        s.img.setPosition(tx, ty - 8).setFlipX(false);
        this.scene.tweens.add({ targets: s.img, y: ty, alpha: 1, duration: 200 });
        return;
      }
      if (tx !== s.img.x) s.img.setFlipX(tx < s.img.x);
      // A sheep through the gate trots on into the pen (more than a tile).
      const far = Math.abs(tx - s.img.x) + Math.abs(ty - s.img.y) > TILE_SIZE * 1.5;
      this.scene.tweens.add({
        targets: s.img,
        x: tx,
        y: ty,
        duration: far ? 320 : 110,
        ease: 'Quad.easeOut',
      });
    });
  }

  /** An expanding ring around the mower (the horn's honk). */
  private ring(): void {
    const circle = this.scene.add
      .circle(this.mower.x, this.mower.y, 6)
      .setStrokeStyle(1, UI.accent)
      .setDepth(MOWER_DEPTH + 1);
    this.scene.tweens.add({
      targets: circle,
      scale: 6,
      alpha: 0,
      duration: 380,
      onComplete: () => circle.destroy(),
    });
  }

  /**
   * Running boosts: a shimmering bubble round the mower (shield), puffs streaming behind it
   * (turbo), a dotted ring at the magnet's reach, critters bobbing as they munch (treat).
   */
  private drawBoosts(px: number, py: number, live: boolean): void {
    const g = this.boostFx;
    g.clear();
    const sim = this.sim;
    const now = this.scene.time.now;
    if (sim.boostTicks('shield') > 0) {
      const pulse = 0.35 + 0.2 * Math.sin(now / 90);
      g.lineStyle(1, 0x74a6ea, pulse + 0.3).strokeCircle(px, py, 13);
      g.fillStyle(0x74a6ea, pulse * 0.4).fillCircle(px, py, 12);
    }
    if (sim.boostTicks('magnet') > 0) {
      const r = (MAGNET_REACH + 0.5) * TILE_SIZE;
      g.fillStyle(0xffd23f, 0.55);
      for (let k = 0; k < 24; k++) {
        const a = (k / 24) * Math.PI * 2 + now / 600;
        g.fillRect(Math.round(px + Math.cos(a) * r), Math.round(py + Math.sin(a) * r), 1, 1);
      }
    }
    if (live && sim.boostTicks('turbo') > 0 && now - this.lastTurboPuff > 70) {
      this.lastTurboPuff = now;
      const d = sim.mower.dir;
      this.fx.puff(px - dx(d) * 9, py - dy(d) * 9);
    }
    const munching = sim.boostTicks('treat') > 0;
    for (const s of this.critterSprites) {
      s.img.setScale(1, munching ? 1 + 0.08 * Math.sin(now / 70) : 1);
    }
  }

  /** The rare find got away: it hops up and vanishes in a sparkle (if it's still on the lawn). */
  rareGotAway(): void {
    (this.sim.level.pickups ?? []).forEach((p, i) => {
      const img = this.pickups[i];
      if (p.kind !== 'rare' || !img) return;
      this.pickups[i] = null;
      this.scene.tweens.killTweensOf(img);
      this.fx.sparkle(p.x, p.y);
      this.scene.tweens.add({
        targets: img,
        y: img.y - 14,
        alpha: 0,
        duration: 500,
        ease: 'Quad.easeIn',
        onComplete: () => img.destroy(),
      });
    });
  }

  /**
   * A dead tree creaks: it sways on its tile, and a dark shadow stretches over the tiles it will
   * fall across (kept until it comes down: it may hold on while the mower is near).
   */
  private creak(index: number): void {
    const f = this.sim.level.falls?.[index];
    if (!f) return;
    const g = this.scene.add.graphics().setDepth(MOWER_DEPTH - 2);
    const ddx = dx(f.dir);
    const ddy = dy(f.dir);
    for (let k = 1; k <= f.length; k++) {
      const x = (f.x + ddx * k) * TILE_SIZE;
      const y = (f.y + ddy * k) * TILE_SIZE;
      g.fillStyle(0x0a1408, 0.38).fillRect(x + 2, y + 2, TILE_SIZE - 4, TILE_SIZE - 4);
    }
    this.creaks.set(index, g);
    this.scene.tweens.add({
      targets: g,
      alpha: { from: 0.4, to: 1 },
      duration: 300,
      yoyo: true,
      repeat: -1,
    });
    // The tree itself sways on its roots, leaning the way it will fall.
    const tree = this.props.get(f.y * this.sim.grid.width + f.x);
    if (tree) {
      const lean = ddx !== 0 ? ddx * 4 : 2;
      this.scene.tweens.add({
        targets: tree,
        angle: { from: -lean * 0.4, to: lean },
        duration: 180,
        yoyo: true,
        repeat: -1,
      });
    }
  }

  /** The tree comes down: the log across its tiles, a stump, a cloud of dust and a thud. */
  private treeFell(e: Extract<SimEvent, { type: 'treeFell' }>): void {
    const g = this.creaks.get(e.index);
    if (g) {
      g.destroy();
      this.creaks.delete(e.index);
    }
    const tree = this.props.get(e.y * this.sim.grid.width + e.x);
    if (tree) this.scene.tweens.killTweensOf(tree);
    const across = dx(e.dir) !== 0;
    const w = this.sim.grid.width;
    e.tiles.forEach((p, k) => {
      const i = p.y * w + p.x;
      this.logs.set(i, k === e.tiles.length - 1 ? LOG_TIP[e.dir] : across ? 'log' : 'logV');
      this.redrawTile(p.x, p.y);
      this.fx.puff((p.x + 0.5) * TILE_SIZE, (p.y + 0.5) * TILE_SIZE);
    });
    this.redrawTile(e.x, e.y);
    this.fx.shake(200, 0.012);
  }

  /** Moles: the lawn trembles where a molehill is about to come up (a few jumping clods). */
  private tremble(x: number, y: number): void {
    const cx = (x + 0.5) * TILE_SIZE;
    const cy = (y + 0.5) * TILE_SIZE;
    // A patch of loosened earth swelling over the warning, then the molehill takes its place.
    const patch = this.scene.add
      .ellipse(cx, cy + 1, 12, 8, 0x4a3220, 0.7)
      .setDepth(MOWER_DEPTH - 2)
      .setScale(0.3);
    this.scene.tweens.add({
      targets: patch,
      scale: 1,
      duration: 1400,
      ease: 'Sine.easeIn',
      onComplete: () => patch.destroy(),
    });
    for (const [ox, oy, delay] of [
      [-4, 2, 0],
      [3, -3, 100],
      [5, 3, 200],
      [-2, -5, 300],
      [0, 5, 400],
    ] as const) {
      const clod = this.scene.add
        .rectangle(cx + ox, cy + oy, 3, 3, 0x6b4a2b)
        .setDepth(MOWER_DEPTH - 2)
        .setAlpha(0);
      this.scene.tweens.add({
        targets: clod,
        alpha: 1,
        y: clod.y - 2,
        duration: 140,
        yoyo: true,
        repeat: 3,
        delay,
        onComplete: () => clod.destroy(),
      });
    }
  }

  /** Moles: the mole pokes its head out of the new molehill, looks about, and ducks back down. */
  private molePeek(x: number, y: number): void {
    const px = (x + 0.5) * TILE_SIZE;
    const py = (y + 0.5) * TILE_SIZE;
    const mole = this.scene.add
      .image(px, py + 4, CRITTERS_KEY, MOLE_FRAME)
      .setDepth(MOWER_DEPTH - 1)
      .setAlpha(0);
    this.scene.tweens.chain({
      targets: mole,
      tweens: [
        { y: py - 2, alpha: 1, duration: 180, ease: 'Back.easeOut' },
        { x: px + 1, duration: 150, yoyo: true, repeat: 1, delay: 200 },
        { y: py + 4, alpha: 0, duration: 200, delay: 400 },
      ],
      onComplete: () => mole.destroy(),
    });
  }

  /** A little "!" that pops above a startled critter. */
  private exclaim(x: number, y: number): void {
    const mark = label(this.scene, x, y - 10, '!', 1, UI.accent)
      .setOrigin(0.5)
      .setDepth(MOWER_DEPTH + 2);
    this.scene.tweens.add({
      targets: mark,
      y: y - 16,
      alpha: 0,
      duration: 500,
      onComplete: () => mark.destroy(),
    });
  }

  private redrawTile(x: number, y: number): void {
    const { grid } = this.sim;
    const i = y * grid.width + x;
    const t = grid.tileAt(x, y);
    const log = this.logs.get(i);
    // A standing thing still there: calm ground under its prop sprite.
    const art = this.art[i] ?? 'grass';
    const prop = propFor(art);
    const standing = prop !== null && t === TILE_BY_ID[art].kind;
    if (prop && standing && !this.props.has(i)) {
      this.props.set(
        i,
        this.scene.add
          .image((x + 0.5) * TILE_SIZE, (y + 1) * TILE_SIZE - PROP_LIFT, PROPS_KEY, propFrame(prop))
          .setOrigin(0.5, 1)
          .setDepth(this.standingDepth(y)),
      );
      this.propRow = -1;
    } else if (!standing && this.props.has(i)) {
      this.props.get(i)!.destroy();
      this.props.delete(i);
    }
    // A standing beehive has its bees buzzing about it; they go with it.
    if (art === 'beehive' && standing && !this.hiveBees.has(i)) this.buzz(i, x, y);
    else if (!standing && this.hiveBees.has(i)) {
      for (const bee of this.hiveBees.get(i)!) {
        this.scene.tweens.killTweensOf(bee);
        bee.destroy();
      }
      this.hiveBees.delete(i);
    }
    // Tall grass stands up off its tile (its blades on their own layer), stubble once thinned.
    const blade =
      art === 'tallGrass' && t === Tile.TallGrass
        ? 0
        : art === 'tallGrass' && t === Tile.RoughGrass
          ? 1
          : -1;
    const tuft = this.blades.get(i);
    if (blade >= 0 && !tuft) {
      this.blades.set(
        i,
        this.scene.add
          .image((x + 0.5) * TILE_SIZE, (y + 1) * TILE_SIZE, BLADES_KEY, blade)
          .setOrigin(0.5, 1)
          .setDepth(this.standingDepth(y)),
      );
    } else if (blade >= 0) tuft?.setFrame(blade);
    else if (tuft) {
      tuft.destroy();
      this.blades.delete(i);
    }
    const frame = standing
      ? frameIndex('lawnBase')
      : log
        ? frameIndex(log)
        : this.smashedTiles.has(i) && art in SMASHED_FRAME
          ? frameIndex(SMASHED_FRAME[art as keyof typeof SMASHED_FRAME])
          : this.art[i] === 'bridge'
            ? frameIndex(this.isWater(x - 1, y) || this.isWater(x + 1, y) ? 'bridgeV' : 'bridge')
            : this.sprinklersOff.has(i)
              ? frameIndex('sprinklerOff')
              : this.trampled.has(i)
                ? frameIndex(this.art[i] === 'green' ? 'greenTrampled' : 'flowerBedTrampled')
                : isHose(t)
                  ? frameIndex(`hose${this.hoseMask(x, y)}` as TileFrameName)
                  : t === Tile.Mowed && this.sim.level.art
                    ? // Lawn Art: the picture's two tones, strongly apart.
                      frameIndex(this.sim.artAxisAt(x, y) === 2 ? 'artLight' : 'artDark')
                    : t === Tile.Mowed && this.stripes
                      ? frameIndex(stripeFrame(this.stripes, x, y))
                      : tileFrame(t, grid.stripeAt(x, y), this.art[i] ?? 'grass', x, y);
    this.layer.putTileAt(frame, x, y);
    if (t === Tile.HoseChewed && !this.chewMarks.has(i)) {
      this.chewMarks.set(
        i,
        this.scene.add
          .image(x * TILE_SIZE, y * TILE_SIZE, TILES_KEY, frameIndex('hoseChewed'))
          .setOrigin(0)
          .setDepth(MOWER_DEPTH - 3),
      );
    }
  }

  private isWater(x: number, y: number): boolean {
    const { width, height } = this.sim.grid;
    return x >= 0 && y >= 0 && x < width && y < height && this.art[y * width + x] === 'water';
  }

  /**
   * Goals (Stadium), in three-quarter view: each run of goal tiles is one goal standing on its
   * goal line, white posts rising off it, a raised crossbar, and a net boxed out behind (away
   * from the pitch). The top goal's net reaches back past the lawn's top edge and stands behind
   * the mower; the bottom goal's net comes toward the camera, so it draws over the mower.
   */
  private drawGoals(): void {
    const { width, height } = this.sim.grid;
    const T = TILE_SIZE;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (this.art[y * width + x] !== 'goal' || (x > 0 && this.art[y * width + x - 1] === 'goal'))
          continue;
        let w = 0;
        while (x + w < width && this.art[y * width + x + w] === 'goal') w++;
        // (The goal line runs along the tiles' middle.)
        this.goalFrame(x * T + 1, (x + w) * T - 1, y * T + T / 2, y < height / 2);
      }
    }
  }

  /**
   * One goal in three-quarter view, standing on its goal line from `left` to `right` (pixels):
   * `far`, the top goal, its net reaching away from us behind the mower; else the bottom goal,
   * its net coming toward the camera, over the mower.
   */
  private goalFrame(
    left: number,
    right: number,
    line: number,
    far: boolean,
  ): Phaser.GameObjects.Graphics {
    const POST = GOAL_POST;
    const DEPTH = GOAL_NET;
    const g = this.scene.add.graphics().setDepth(far ? MOWER_DEPTH - 1.6 : MOWER_DEPTH + 0.6);
    const top = line - POST;
    // The net box: its back frame DEPTH pixels behind the goal line (up the screen for the
    // top goal; down it, toward us, for the bottom one), its roof and sides meshed.
    const back = far ? -DEPTH : DEPTH;
    // A net panel: a soft shade (the net's own shadow, so it reads over grass or fence) under a
    // white mesh of knots.
    const mesh = (x0: number, y0: number, x1: number, y1: number) => {
      const ya = Math.min(y0, y1);
      const yb = Math.max(y0, y1);
      g.fillStyle(0x0a1a12, 0.22).fillRect(x0, ya, x1 - x0 + 1, yb - ya + 1);
      for (let yy = ya; yy <= yb; yy += 2) {
        for (let xx = x0; xx <= x1; xx += 2) {
          g.fillStyle(0xffffff, ((xx + yy) & 2) === 0 ? 0.8 : 0.5).fillRect(xx, yy, 1, 1);
        }
      }
    };
    // Shadow of the frame on the lawn.
    g.fillStyle(0x0a3c18, 0.28).fillRect(left + 2, line + 1, right - left, 2);
    if (far) {
      // Behind the frame: the back panel and roof first, then the posts and bar over them.
      mesh(left + 2, top + back, right - 2, line + back);
      mesh(left, top + back, right, top);
      g.fillStyle(0xd5d8e4).fillRect(left + 2, top + back, right - left - 4, 1);
      for (const px of [left + 2, right - 3])
        g.fillStyle(0xd5d8e4).fillRect(px, top + back, 1, POST);
    }
    // The frame: two posts and the crossbar, white, lit on the left, outlined.
    const ink = 0x10180c;
    for (const px of [left, right - 2]) {
      g.fillStyle(ink).fillRect(px - 1, top - 1, 4, POST + 2);
      g.fillStyle(0xffffff).fillRect(px, top, 1, POST + 1);
      g.fillStyle(0xcfcad8).fillRect(px + 1, top, 1, POST + 1);
    }
    g.fillStyle(ink).fillRect(left - 1, top - 1, right - left + 2, 4);
    g.fillStyle(0xffffff).fillRect(left, top, right - left, 1);
    g.fillStyle(0xcfcad8).fillRect(left, top + 1, right - left, 1);
    if (!far) {
      // In front of the frame: the roof sloping toward us, then the front panel of the net
      // hanging down to the lawn, and its bottom rope.
      mesh(left, top + 2, right, top + back);
      mesh(left + 2, top + back, right - 2, line + back);
      g.fillStyle(0xd5d8e4).fillRect(left + 2, top + back, right - left - 4, 1);
      g.fillStyle(0xd5d8e4).fillRect(left + 2, line + back, right - left - 4, 1);
      for (const px of [left + 2, right - 3])
        g.fillStyle(0xd5d8e4).fillRect(px, top + back, 1, POST);
    }
    return g;
  }

  // ── Mower soccer ─────────────────────────────────────────────────────────────────────────

  /**
   * The pitch: a goal standing in each gap of the fence (top and bottom, its goal line on the
   * lawn's edge), and the chalk: the halfway line, the center circle and spot, and a box in front
   * of each goal.
   */
  private drawPitch(mouth: [number, number]): void {
    const soccer = this.sim.level.soccer!;
    const T = TILE_SIZE;
    const w = this.mapWidthPx;
    const h = this.mapHeightPx;
    this.pitchGoals = [
      this.goalFrame(mouth[0] + 1, mouth[1] - 1, 0, true),
      this.goalFrame(mouth[0] + 1, mouth[1] - 1, h, false),
    ];
    const g = this.scene.add.graphics().setDepth(MOWER_DEPTH - 3);
    const cx = (soccer.center.x + 0.5) * T;
    const cy = (soccer.center.y + 0.5) * T;
    g.fillStyle(0xf4f1e6, 0.7);
    // The halfway line and the boxes, a pixel wide.
    g.fillRect(0, Math.round(cy), w, 1);
    const boxL = mouth[0] - 2 * T;
    const boxR = mouth[1] + 2 * T;
    const boxH = 3 * T;
    for (const [y0, y1] of [
      [0, boxH],
      [h - boxH, h],
    ] as const) {
      g.fillRect(boxL, y0, 1, y1 - y0);
      g.fillRect(boxR - 1, y0, 1, y1 - y0);
      g.fillRect(boxL, y0 === 0 ? y1 : y0, boxR - boxL, 1);
    }
    // The center circle, pixel by pixel, and the spot.
    const r = 2.5 * T;
    for (let a = 0; a < 360; a += 2) {
      const px = Math.round(cx + r * Math.cos((a * Math.PI) / 180));
      const py = Math.round(cy + r * Math.sin((a * Math.PI) / 180));
      g.fillRect(px, py, 1, 1);
    }
    g.fillRect(Math.round(cx) - 1, Math.round(cy) - 1, 3, 3);
  }

  /**
   * The soccer ball: white with black patches that roll round as it goes, a shadow under it; it
   * drops onto the spot after a goal.
   */
  private drawPitchBall(alpha: number): void {
    const g = this.pitchBallFx;
    if (!g) return;
    g.clear();
    const b = this.sim.pitchBall;
    if (!b) return;
    const T = TILE_SIZE;
    const t = b.left > 0 ? Math.min(1, (b.progress + alpha) / BALL_TICKS) : 0;
    const px = Math.round((b.x + 0.5 + dx(b.dir) * t) * T);
    const py = Math.round((b.y + 0.5 + dy(b.dir) * t) * T);
    // Back on the spot: it drops in from a little above and lands.
    const since = this.scene.time.now - this.ballPoppedAt;
    const lift = since < 320 ? Math.round(10 * (1 - since / 320) ** 2) : 0;
    g.fillStyle(0x0a1a08, 0.35).fillEllipse(px + 1, py + 4, 11 - lift / 3, 4);
    const by = py - lift;
    g.fillStyle(0x10180c).fillCircle(px, by, 5.5);
    g.fillStyle(0xffffff).fillCircle(px, by, 4.5);
    const spin = Math.floor((b.x + b.y + t * (dx(b.dir) + dy(b.dir))) * 4 + 400) % 4;
    const spots: [number, number][] = [
      [-2, -1],
      [1, -2],
      [2, 1],
      [-1, 2],
    ];
    g.fillStyle(0x222326);
    for (const k of [spin, (spin + 2) % 4]) {
      const [sx, sy] = spots[k]!;
      g.fillRect(px + sx - 1, by + sy - 1, 2, 2);
    }
  }

  /**
   * GOAL! The ball flies on into the net and rests there a moment, the net billows, and confetti
   * bursts from the goal.
   */
  private scoreGoal(x: number, y: number): void {
    const T = TILE_SIZE;
    const top = y < this.sim.grid.height / 2;
    const net = this.pitchGoals[top ? 0 : 1];
    const sx = (x + 0.5) * T;
    const sy = (y + 0.5) * T;
    const ball = this.scene.add.graphics().setDepth(top ? MOWER_DEPTH - 1.7 : MOWER_DEPTH + 0.5);
    ball.fillStyle(0x10180c).fillCircle(0, 0, 5.5);
    ball.fillStyle(0xffffff).fillCircle(0, 0, 4.5);
    ball.fillStyle(0x222326).fillRect(-2, -2, 2, 2).fillRect(1, 1, 2, 2);
    ball.setPosition(sx, sy);
    this.scene.tweens.add({
      targets: ball,
      y: top ? -GOAL_NET - 4 : this.mapHeightPx + GOAL_NET - 2,
      duration: 220,
      ease: 'Quad.easeOut',
    });
    this.scene.tweens.add({
      targets: ball,
      alpha: 0,
      delay: 900,
      duration: 300,
      onComplete: () => ball.destroy(),
    });
    if (net) {
      // The net billows back with the ball, then settles.
      this.scene.tweens.add({
        targets: net,
        y: top ? -2 : 2,
        delay: 120,
        duration: 110,
        yoyo: true,
        repeat: 1,
        ease: 'Sine.easeOut',
        onComplete: () => net.setY(0),
      });
    }
    this.fx.goalBurst(sx, top ? 0 : this.mapHeightPx);
    this.fx.shake(160, 0.006);
  }

  /**
   * Houses and barns: each block of their tiles drawn as one building in three-quarter view, its
   * roof rising above its front wall (the roof may reach over the lawn's top edge).
   */
  private drawBuildings(): void {
    const { width, height } = this.sim.grid;
    const seen = new Set<number>();
    const T = TILE_SIZE;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const id = this.art[y * width + x];
        if ((id !== 'house' && id !== 'barn') || seen.has(y * width + x)) continue;
        let w = 0;
        while (x + w < width && this.art[y * width + x + w] === id) w++;
        let h = 0;
        while (y + h < height && this.art[(y + h) * width + x] === id) h++;
        for (let yy = y; yy < y + h; yy++)
          for (let xx = x; xx < x + w; xx++) seen.add(yy * width + xx);
        const g = this.scene.add.graphics().setDepth(MOWER_DEPTH - 1.6);
        // The backyard's block on the top fence is the big house's back deck (owner, 2026-10-07:
        // a little house in the yard clashed with the real-sized one behind the fence).
        if (id === 'house') drawDeck(g, x * T, y * T, w * T, h * T);
        else drawBuilding(g, id, x * T, y * T, w * T, h * T);
      }
    }
  }

  /**
   * Edges drawn over the tiles: bridge rails where a deck meets the water, and the maze's hedges
   * standing tall (a lit top, a dark front face and a shadow on the path below).
   */
  private drawEdges(): void {
    const { width, height } = this.sim.grid;
    const at = (x: number, y: number) =>
      x < 0 || y < 0 || x >= width || y >= height ? null : this.art[y * width + x];
    const edged = ['bridge', 'mazeHedge', 'hedge', 'fieldLine', 'goal'] as const;
    if (!edged.some((id) => this.art.includes(id))) return;
    const g = this.scene.add.graphics().setDepth(MOWER_DEPTH - 4);
    const T = TILE_SIZE;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const id = at(x, y);
        const px = x * T;
        const py = y * T;
        if (id === 'bridge') {
          const rail = (rx: number, ry: number, rw: number, rh: number) => {
            g.fillStyle(0x3d2a18).fillRect(rx, ry, rw, rh);
            g.fillStyle(0xd9b48a).fillRect(
              rx + (rw > rh ? 0 : 1),
              ry + (rw > rh ? 1 : 0),
              rw > rh ? rw : 1,
              rw > rh ? 1 : rh,
            );
          };
          if (at(x - 1, y) === 'water') rail(px, py, 3, T);
          if (at(x + 1, y) === 'water') rail(px + T - 3, py, 3, T);
          if (at(x, y - 1) === 'water') rail(px, py, T, 3);
          if (at(x, y + 1) === 'water') rail(px, py + T - 3, T, 3);
        } else if (id === 'fieldLine') {
          // Chalk: a 2 px line through the tile, joined to the lines beside it.
          g.fillStyle(0xf4f1de, 0.85);
          const joins = [
            [x - 1, y, px, py + 7, 9, 2],
            [x + 1, y, px + 7, py + 7, 9, 2],
            [x, y - 1, px + 7, py, 2, 9],
            [x, y + 1, px + 7, py + 7, 2, 9],
          ] as const;
          let any = false;
          for (const [nx, ny, rx, ry, rw, rh] of joins) {
            if (at(nx, ny) !== 'fieldLine') continue;
            g.fillRect(rx, ry, rw, rh);
            any = true;
          }
          if (!any) g.fillRect(px + 6, py + 6, 4, 4);
        } else if (id === 'goal' && x === Math.floor(width / 2) && y === 0) {
          // A pitch: the center circle round the middle of the halfway line, in chalk.
          const cx = (width / 2) * T;
          const cy = (Math.floor(height / 2) + 0.5) * T;
          const r = T * 2.4;
          g.fillStyle(0xf4f1de, 0.85);
          for (let a = 0; a < 360; a += 3) {
            const rad = (a * Math.PI) / 180;
            g.fillRect(
              Math.round(cx + Math.cos(rad) * r) - 1,
              Math.round(cy + Math.sin(rad) * r) - 1,
              2,
              2,
            );
          }
          g.fillRect(Math.round(cx) - 2, Math.round(cy) - 2, 4, 4);
        } else if (id === 'mazeHedge' || id === 'hedge') {
          // A hedge standing tall: lit top, dark front face, shadow on the lawn below it. (A
          // trimmed hedgerow tile is ground now: the sim knows, so skip it.)
          if (this.sim.grid.tileAt(x, y) !== TILE_BY_ID[id].kind) continue;
          const same = (ax: number, ay: number) =>
            at(ax, ay) === id && this.sim.grid.tileAt(ax, ay) === TILE_BY_ID[id].kind;
          if (!same(x, y - 1)) g.fillStyle(0x86cf52, 0.9).fillRect(px, py, T, 2);
          if (!same(x, y + 1)) {
            g.fillStyle(0x0d2a12).fillRect(px, py + T - 4, T, 4);
            if (y + 1 < height) g.fillStyle(0x0a1a08, 0.35).fillRect(px, py + T, T, 3);
          }
          if (!same(x - 1, y) && x > 0) g.fillStyle(0x0d2a12, 0.6).fillRect(px, py, 1, T);
          if (!same(x + 1, y) && x + 1 < width)
            g.fillStyle(0x0d2a12, 0.6).fillRect(px + T - 1, py, 1, T);
        }
      }
    }
  }

  /**
   * Hills (lm-875): each a grassy mound (hillArt.ts) laid over its tiles' own grass, lit from the
   * top left, a flat summit on its middle tile. Toward the summit is uphill; away is down.
   */
  private drawHills(): void {
    for (const hill of this.sim.level.hills ?? []) {
      const { shade, light } = hillTextures(this.scene, hill.w, hill.h);
      for (const [key, blend] of [
        [shade, 'MULTIPLY'],
        [light, 'ADD'],
      ] as const) {
        this.scene.add
          .image(hill.x * TILE_SIZE, hill.y * TILE_SIZE, key)
          .setOrigin(0, 0)
          .setBlendMode(blend)
          .setDepth(MOWER_DEPTH - 4);
      }
    }
  }

  /** Each hose's free end (a tile joined on one side only), and the hose's tiles. */
  private findHoseEnds(): void {
    const { grid } = this.sim;
    const W = grid.width;
    this.hoseEnds = [];
    const BITS = [
      [HOSE_BIT.N, 0, -1],
      [HOSE_BIT.E, 1, 0],
      [HOSE_BIT.S, 0, 1],
      [HOSE_BIT.W, -1, 0],
    ] as const;
    for (let y = 0; y < grid.height; y++) {
      for (let x = 0; x < W; x++) {
        if (!isHose(grid.tileAt(x, y))) continue;
        const link = BITS.find(([bit]) => this.hoseMask(x, y) === bit);
        if (!link) continue;
        // Every tile of this hose, from its free end.
        const tiles: number[] = [];
        const seen = new Set<number>([y * W + x]);
        const queue: [number, number][] = [[x, y]];
        while (queue.length) {
          const [cx, cy] = queue.shift()!;
          tiles.push(cy * W + cx);
          for (const [, ddx, ddy] of BITS) {
            const nx = cx + ddx;
            const ny = cy + ddy;
            if (!grid.inBounds(nx, ny) || !isHose(grid.tileAt(nx, ny)) || seen.has(ny * W + nx)) {
              continue;
            }
            seen.add(ny * W + nx);
            queue.push([nx, ny]);
          }
        }
        this.hoseEnds.push({ x, y, dx: -link[1], dy: -link[2], tiles });
      }
    }
  }

  /**
   * Water from each hose's nozzle: a little arc of droplets. Once the mower has chewed the hose
   * the end goes dry and the water squirts up from the damage instead.
   */
  private drawHoseWater(g: Phaser.GameObjects.Graphics, now: number): void {
    const { grid } = this.sim;
    const W = grid.width;
    for (const end of this.hoseEnds) {
      const chewed = end.tiles.filter(
        (i) => grid.tileAt(i % W, Math.floor(i / W)) === Tile.HoseChewed,
      );
      if (chewed.length === 0) {
        // The nozzle's tip, then an arc of water out and down (sideways ones arc up first):
        // chunky drops near the nozzle, fine spray further out.
        const tipX = (end.x + 0.5) * TILE_SIZE + end.dx * 5;
        const tipY = (end.y + 0.5) * TILE_SIZE + end.dy * 5;
        for (let k = 0; k < 10; k++) {
          const t = (now / 560 + k / 10) % 1;
          const reach = t * 14;
          const lift = end.dy === 0 ? Math.sin(t * Math.PI) * 6 : 0;
          const spread = end.dy === 0 ? 0 : Math.sin(k * 2.1) * t * 4;
          const px = tipX + end.dx * reach + spread;
          const py = tipY + end.dy * reach - lift + (end.dy === 0 ? t * 4 : 0);
          const size = t < 0.45 ? 2 : 1;
          g.fillStyle(k % 3 === 0 ? 0xffffff : 0x8fd0ff, 1 - t * 0.5).fillRect(
            Math.round(px),
            Math.round(py),
            size,
            size,
          );
        }
        continue;
      }
      // A squirt up from each chewed spot.
      for (const i of chewed) {
        const cx = ((i % W) + 0.5) * TILE_SIZE;
        const cy = (Math.floor(i / W) + 0.5) * TILE_SIZE;
        for (let k = 0; k < 9; k++) {
          const t = (now / 400 + k / 9) % 1;
          const side = (k % 2 === 0 ? 1 : -1) * (1 + (k % 3));
          const size = t < 0.4 ? 2 : 1;
          g.fillStyle(k % 3 === 0 ? 0xffffff : 0x8fd0ff, 1 - t * 0.5).fillRect(
            Math.round(cx + side * t * 2.5),
            Math.round(cy - 2 - Math.sin(t * Math.PI) * 9),
            size,
            size,
          );
        }
      }
    }
  }

  /**
   * Which sides a hose tile joins its neighbors through (HOSE_BIT). An end on the lawn's edge
   * runs out under the fence, to the tap.
   */
  private hoseMask(x: number, y: number): number {
    const { grid } = this.sim;
    let mask = 0;
    let links = 0;
    for (const [bit, ddx, ddy] of [
      [HOSE_BIT.N, 0, -1],
      [HOSE_BIT.E, 1, 0],
      [HOSE_BIT.S, 0, 1],
      [HOSE_BIT.W, -1, 0],
    ] as const) {
      if (isHose(grid.tileAt(x + ddx, y + ddy)) && grid.inBounds(x + ddx, y + ddy)) {
        mask |= bit;
        links++;
      }
    }
    if (links <= 1) {
      if (y === 0 && !(mask & HOSE_BIT.N)) mask |= HOSE_BIT.N;
      else if (y === grid.height - 1 && !(mask & HOSE_BIT.S)) mask |= HOSE_BIT.S;
      else if (x === 0 && !(mask & HOSE_BIT.W)) mask |= HOSE_BIT.W;
      else if (x === grid.width - 1 && !(mask & HOSE_BIT.E)) mask |= HOSE_BIT.E;
    }
    return mask;
  }

  /** A quick blink (a knock with no safe window after it, e.g. a sting under the shield). */
  private blinkMower(): void {
    if (this.sim.mower.invulnTicks > 0) return; // render() blinks it for the whole window
    this.scene.tweens.add({
      targets: this.mower,
      alpha: { from: 0.2, to: 1 },
      duration: 90,
      repeat: 3,
    });
  }

  /** Whether render() is blinking the mower through a safe window. */
  private blinking = false;
}

/**
 * A white picket fence round the lawn: the hard, bright edge of the playfield
 * (docs/ART_DIRECTION.md). Drawn once; it never changes.
 */
function drawPicketFence(
  scene: Phaser.Scene,
  w: number,
  h: number,
  yard: SecretYard | null,
  goalMouth: [number, number] | null = null,
): void {
  const g = scene.add.graphics();
  const ink = 0x10180c;
  const white = 0xffffff;
  const shade = 0xcfcad8;
  // A secret yard's gap: the span of fence its gate stands in (pixels along that side). Mower
  // soccer: the goal mouths at the top and bottom (the goals stand in them).
  const gap = (side: SecretYard['side']): [number, number] | null => {
    if (goalMouth && (side === 'top' || side === 'bottom')) return goalMouth;
    if (yard?.side !== side) return null;
    const at = side === 'top' || side === 'bottom' ? yard.gate.x : yard.gate.y;
    return [at * TILE_SIZE, (at + 1) * TILE_SIZE];
  };
  const inGap = (span: [number, number] | null, a: number, b: number) =>
    span !== null && b > span[0] && a < span[1];
  // Top and bottom: pickets with pointed tops on a rail.
  for (const [y, side] of [
    [-9, 'top'],
    [h + 1, 'bottom'],
  ] as const) {
    const span = gap(side);
    const rail = (a: number, b: number) => {
      g.fillStyle(ink).fillRect(a, y + 2, b - a, 4);
      g.fillStyle(shade).fillRect(a, y + 3, b - a, 2);
    };
    if (span) {
      rail(-6, span[0]);
      rail(span[1], w + 6);
    } else rail(-6, w + 6);
    for (let x = -6; x < w + 6; x += 6) {
      if (inGap(span, x, x + 5)) continue;
      g.fillStyle(ink).fillRect(x, y, 5, 8);
      g.fillStyle(white).fillRect(x + 1, y + 1, 3, 6);
      g.fillStyle(shade).fillRect(x + 1, y + 6, 3, 1);
    }
    if (span && !goalMouth) gatePosts(g, span[0] - 3, y - 2, span[1] - 2, y - 2, 12);
  }
  // Sides: a rail with posts. A full-width lawn fills the screen exactly, so the side fences
  // overlap the lawn's outer pixels (drawn above the tiles) to stay on screen: the lawn plainly
  // ends there.
  const sides = scene.add.graphics().setDepth(MOWER_DEPTH - 1);
  for (const [x, side] of [
    [-2, 'left'],
    [w - 3, 'right'],
  ] as const) {
    const span = gap(side);
    const rail = (a: number, b: number) => {
      sides.fillStyle(ink).fillRect(x, a, 5, b - a);
      sides.fillStyle(white).fillRect(x + 1, a + 1, 3, b - a - 2);
    };
    if (span) {
      rail(-9, span[0]);
      rail(span[1], h + 9);
    } else rail(-9, h + 9);
    for (let y = -2; y < h + 4; y += 8) {
      if (inGap(span, y, y + 4)) continue;
      sides.fillStyle(shade).fillRect(x + 1, y, 3, 1);
      sides.fillStyle(ink).fillRect(x, y + 3, 5, 1); // a post every half tile
    }
    if (span) gatePosts(sides, x - 1, span[0] - 4, x - 1, span[1] - 2, 6);
  }
}

/** The mower's sprite sheet in play: its paint, and its gold Master trim once mastered. */
function mowerSheet(
  scene: Phaser.Scene,
  id: MowerId,
  paint: string | null,
  master: boolean,
): string {
  const key = paint ? paintedMowerTexture(scene, id, paint) : mowerTextureKey(id);
  return master ? masterTrimTexture(scene, key) : key;
}

/** Mower soccer: the goal mouths' span along the top and bottom edges (pixels), or null. */
function soccerMouthSpan(sim: Simulation): [number, number] | null {
  const mouths = sim.level.soccer?.mouths;
  if (!mouths || mouths.length === 0) return null;
  const xs = mouths.map((p) => p.x);
  return [Math.min(...xs) * TILE_SIZE, (Math.max(...xs) + 1) * TILE_SIZE];
}

/** The two stout posts either side of a gap in the fence (a secret yard's gate). */
function gatePosts(
  g: Phaser.GameObjects.Graphics,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  tall: number,
): void {
  for (const [x, y] of [
    [x1, y1],
    [x2, y2],
  ] as const) {
    g.fillStyle(0x10180c).fillRect(x, y, 7, tall);
    g.fillStyle(0x8b5a2b).fillRect(x + 1, y + 1, 5, tall - 2);
    g.fillStyle(0xb07a43).fillRect(x + 1, y + 1, 2, tall - 2);
    g.fillStyle(0xe2ddd2).fillRect(x, y - 1, 7, 2);
  }
}

/**
 * Boss herding: a post-and-rail fence round the sheep pen, open on the side facing the gate (with a
 * post either side of the opening).
 */
function drawPenFence(scene: Phaser.Scene, herd: HerdSpec): void {
  const g = scene.add.graphics().setDepth(MOWER_DEPTH - 2);
  const inPen = (x: number, y: number) => herd.pen.some((p) => p.x === x && p.y === y);
  const T = TILE_SIZE;
  const rail = (x: number, y: number, w: number, h: number) => {
    g.fillStyle(0x10180c).fillRect(x - 1, y - 1, w + 2, h + 2);
    g.fillStyle(0xead2a4).fillRect(x, y, w, h);
  };
  const posts: [number, number][] = [];
  for (const p of herd.pen) {
    const px = p.x * T;
    const py = p.y * T;
    for (const [nx, ny, side] of [
      [p.x, p.y - 1, 'N'],
      [p.x + 1, p.y, 'E'],
      [p.x, p.y + 1, 'S'],
      [p.x - 1, p.y, 'W'],
    ] as const) {
      if (inPen(nx, ny)) continue;
      const gate = herd.gate.x === nx && herd.gate.y === ny;
      if (side === 'N' || side === 'S') {
        const y = side === 'N' ? py + 1 : py + T - 3;
        if (!gate) rail(px, y, T, 2);
        posts.push([px, y - 2], [px + T - 2, y - 2]);
      } else {
        const x = side === 'W' ? px + 1 : px + T - 3;
        if (!gate) rail(x, py, 2, T);
        posts.push([x, py], [x, py + T - 4]);
      }
    }
  }
  for (const [x, y] of posts) {
    g.fillStyle(0x10180c).fillRect(x - 1, y - 1, 4, 6);
    g.fillStyle(0x8a5a2b).fillRect(x, y, 2, 4);
  }
}

/** Boss storm: the garden shed waiting above the top fence, with a chequered finish line. */
function drawShed(scene: Phaser.Scene, mapWidthPx: number): void {
  const g = scene.add.graphics().setDepth(MOWER_DEPTH - 1);
  const cx = Math.round(mapWidthPx / 2);
  // Shed: plank walls, a red roof, an open door.
  const w = 40;
  const x0 = cx - w / 2;
  g.fillStyle(0x10180c).fillRect(x0 - 1, -31, w + 2, 26);
  g.fillStyle(0xb98249).fillRect(x0, -30, w, 24);
  for (let x = x0 + 4; x < x0 + w; x += 5) g.fillStyle(0x9c6a3c).fillRect(x, -30, 1, 24);
  g.fillStyle(0x10180c).fillTriangle(x0 - 4, -29, cx, -44, x0 + w + 4, -29);
  g.fillStyle(0xc8553d).fillTriangle(x0 - 2, -30, cx, -42, x0 + w + 2, -30);
  g.fillStyle(0x2a1c12).fillRect(cx - 6, -20, 12, 14);
  // Finish line along the top edge of the lawn.
  for (let x = 0; x < mapWidthPx; x += 4) {
    g.fillStyle((x / 4) % 2 === 0 ? 0xffffff : 0x10180c).fillRect(x, -3, 4, 2);
    g.fillStyle((x / 4) % 2 === 0 ? 0x10180c : 0xffffff).fillRect(x, -1, 4, 1);
  }
}

/** A fallen tree's top end, by the way it fell (N, E, S, W). */
const LOG_TIP = [
  'logTipN',
  'logTip',
  'logTipS',
  'logTipW',
] as const satisfies readonly TileFrameName[];

const INK = 0x10180c;

/**
 * The house's back deck (the yard's 'house' block): planks, a railing with posts and a gap for the
 * steps down to the patio, a patio table under a striped umbrella with two chairs, a kettle grill.
 */
function drawDeck(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const plank = 0xb07a45;
  const plankDark = 0x8a5a30;
  const plankLit = 0xd39a5e;
  const rail = 0xe0b077;
  // Shadow under the front edge, the floor, plank seams.
  g.fillStyle(0x0a3c18, 0.32).fillRect(x + 2, y + h, w - 2, 3);
  g.fillStyle(INK).fillRect(x - 1, y - 1, w + 2, h + 2);
  g.fillStyle(plank).fillRect(x, y, w, h);
  for (let py = y + 3; py < y + h; py += 4) g.fillStyle(plankDark).fillRect(x, py, w, 1);
  for (let py = y + 1; py < y + h; py += 4) g.fillStyle(plankLit).fillRect(x, py, w, 1);
  // The front edge: a deep fascia board.
  g.fillStyle(plankDark).fillRect(x, y + h - 3, w, 3);
  // Railing: posts and a top rail along the sides and the front, a gap at the middle for the steps.
  const mid = Math.round(x + w / 2);
  const post = (px: number, py: number) => {
    g.fillStyle(INK).fillRect(px - 1, py - 7, 4, 9);
    g.fillStyle(rail).fillRect(px, py - 6, 2, 7);
  };
  const frontY = y + h - 2;
  for (let px = x; px <= x + w - 2; px += 8) if (Math.abs(px + 1 - mid) > 6) post(px, frontY);
  post(x + w - 2, frontY);
  g.fillStyle(INK).fillRect(x - 1, frontY - 8, mid - 6 - x + 1, 3);
  g.fillStyle(rail).fillRect(x, frontY - 7, mid - 7 - x, 1);
  g.fillStyle(INK).fillRect(mid + 6, frontY - 8, x + w - mid - 5, 3);
  g.fillStyle(rail).fillRect(mid + 7, frontY - 7, x + w - mid - 8, 1);
  for (const sx of [x, x + w - 2]) {
    g.fillStyle(INK).fillRect(sx - 1, y - 2, 4, h - 6);
    g.fillStyle(rail).fillRect(sx, y - 1, 2, h - 8);
  }
  // Steps down to the patio.
  g.fillStyle(INK).fillRect(mid - 6, y + h - 1, 12, 5);
  g.fillStyle(plankLit).fillRect(mid - 5, y + h, 10, 1);
  g.fillStyle(plank).fillRect(mid - 5, y + h + 1, 10, 2);
  // A patio table under an umbrella, a chair either side (left third).
  const tx = Math.round(x + w * 0.3);
  const ty = y + 13;
  g.fillStyle(0x0a3c18, 0.3).fillEllipse(tx + 2, ty + 5, 22, 8);
  g.fillStyle(INK)
    .fillRect(tx - 9, ty - 1, 5, 6)
    .fillRect(tx + 4, ty - 1, 5, 6);
  g.fillStyle(0xf4f1de)
    .fillRect(tx - 8, ty, 3, 4)
    .fillRect(tx + 5, ty, 3, 4);
  g.fillStyle(INK).fillEllipse(tx, ty + 1, 11, 7);
  g.fillStyle(0xfffaf0).fillEllipse(tx, ty + 1, 9, 5);
  g.fillStyle(INK).fillRect(tx - 1, ty - 9, 2, 10);
  // The umbrella: red and white panels, seen from above at a slant.
  g.fillStyle(INK).fillEllipse(tx, ty - 10, 21, 10);
  g.fillStyle(0xe2463a).fillEllipse(tx, ty - 10, 19, 8);
  g.fillStyle(0xfffaf0)
    .fillTriangle(tx, ty - 10, tx - 9, ty - 11, tx - 5, ty - 14)
    .fillTriangle(tx, ty - 10, tx + 9, ty - 11, tx + 5, ty - 14)
    .fillTriangle(tx, ty - 10, tx - 3, ty - 6, tx + 3, ty - 6);
  g.fillStyle(INK).fillRect(tx - 1, ty - 12, 2, 2);
  // A kettle grill (right side): a black bowl on legs, a lid handle.
  const gx = Math.round(x + w * 0.78);
  const gy = y + 12;
  g.fillStyle(0x0a3c18, 0.3).fillEllipse(gx + 1, gy + 8, 12, 4);
  g.fillStyle(INK)
    .fillRect(gx - 4, gy + 2, 1, 6)
    .fillRect(gx + 3, gy + 2, 1, 6);
  g.fillStyle(INK).fillEllipse(gx, gy, 11, 9);
  g.fillStyle(0x3a3f47).fillEllipse(gx, gy, 9, 7);
  g.fillStyle(0x6a717c).fillRect(gx - 3, gy - 3, 3, 1);
  g.fillStyle(0xc0c4cc).fillRect(gx - 1, gy - 5, 2, 1);
}

/**
 * A house or barn filling a block of tiles (x, y, w, h in pixels), in three-quarter view: the
 * front wall along the bottom, the roof sloping up from it over the rest (and a little above),
 * lit from the top left, outlined in ink.
 */
function drawBuilding(
  g: Phaser.GameObjects.Graphics,
  kind: 'house' | 'barn',
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const ink = 0x10180c;
  const wallH = Math.min(14, Math.round(h * 0.5));
  const wallY = y + h - wallH;
  const roofTop = y - 6;
  const barn = kind === 'barn';
  const wall = barn ? 0xc8303a : 0xf2e4c4;
  const wallShade = barn ? 0x8e1f28 : 0xd6c49c;
  const roof = barn ? 0x5a5f6e : 0xc8553d;
  const roofLit = barn ? 0x8a90a8 : 0xff8a6a;
  const roofDark = barn ? 0x3a3d4a : 0x8e3324;
  // A soft shadow on the lawn below the wall.
  g.fillStyle(0x0a3c18, 0.32).fillRect(x + 2, y + h, w - 2, 3);
  // The roof: a sloped face with shingle rows, a lit ridge, overhanging the wall a little.
  g.fillStyle(ink).fillRect(x - 2, roofTop - 1, w + 4, wallY - roofTop + 3);
  g.fillStyle(roof).fillRect(x - 1, roofTop, w + 2, wallY - roofTop + 1);
  for (let ry = roofTop + 3; ry < wallY; ry += 3)
    g.fillStyle(roofDark).fillRect(x - 1, ry, w + 2, 1);
  g.fillStyle(roofLit).fillRect(x - 1, roofTop, w + 2, 2);
  g.fillStyle(roofLit).fillRect(x - 1, roofTop, 2, wallY - roofTop);
  // The front wall.
  g.fillStyle(ink).fillRect(x - 1, wallY, w + 2, wallH + 1);
  g.fillStyle(wall).fillRect(x, wallY + 1, w, wallH - 1);
  g.fillStyle(wallShade).fillRect(x, wallY + 1, w, 2);
  if (!barn) {
    // Siding lines, windows either side, a door in the middle, a chimney on the roof.
    for (let ly = wallY + 4; ly < y + h; ly += 3) g.fillStyle(wallShade).fillRect(x, ly, w, 1);
    for (let wx = x + 5; wx + 8 <= x + w - 4; wx += Math.max(16, Math.floor(w / 3))) {
      if (Math.abs(wx + 4 - (x + w / 2)) < 7) continue;
      g.fillStyle(ink).fillRect(wx - 1, wallY + 3, 10, 8);
      g.fillStyle(0x6fa0ff).fillRect(wx, wallY + 4, 8, 6);
      g.fillStyle(0xd9f4ff).fillRect(wx, wallY + 4, 3, 2);
      g.fillStyle(0xfffaf0).fillRect(wx + 3, wallY + 4, 1, 6);
    }
    const dx = Math.round(x + w / 2 - 3);
    g.fillStyle(ink).fillRect(dx - 1, wallY + 2, 8, wallH - 1);
    g.fillStyle(0x9a5232).fillRect(dx, wallY + 3, 6, wallH - 2);
    g.fillStyle(0xffd23f).fillRect(dx + 4, wallY + 8, 1, 1);
    g.fillStyle(ink).fillRect(x + w - 12, roofTop - 6, 7, 8);
    g.fillStyle(0x9a5232).fillRect(x + w - 11, roofTop - 5, 5, 7);
  } else {
    // Board lines, a big white-braced door in the middle, a hay loft window above it.
    for (let bx = x + 3; bx < x + w; bx += 4)
      g.fillStyle(wallShade).fillRect(bx, wallY + 3, 1, wallH - 3);
    const dw = Math.min(16, w - 8);
    const dx = Math.round(x + w / 2 - dw / 2);
    g.fillStyle(ink).fillRect(dx - 1, wallY + 1, dw + 2, wallH);
    g.fillStyle(0x8e1f28).fillRect(dx, wallY + 2, dw, wallH - 2);
    g.lineStyle(1, 0xfffaf0);
    g.strokeRect(dx + 0.5, wallY + 2.5, dw - 1, wallH - 3);
    g.lineBetween(dx, wallY + 2, dx + dw, wallY + wallH);
    g.lineBetween(dx + dw, wallY + 2, dx, wallY + wallH);
    g.fillStyle(ink).fillRect(Math.round(x + w / 2) - 4, roofTop + 4, 8, 7);
    g.fillStyle(0xe2bf55).fillRect(Math.round(x + w / 2) - 3, roofTop + 5, 6, 5);
  }
}
